/* =================================================================
   一沙一世界 · 主 App
   ================================================================= */

function App() {
  const [state, renderState] = useState(() => SandPlatform.initial());
  const setState = SandPlatform.update;
  usePlatformStatus();
  const [view, setView] = useState(() => {
    const session = state.session;
    if (session?.kind === 'guest' || session?.kind === 'showcase') return 'showcase';
    if (session?.kind === 'student' && state.students?.[session.id]) return 'student';
    if (session?.kind === 'agent' && ROLES_BY_ID[session.roleId]) return 'agent';
    return session?.kind === 'teacher' ? 'teacher' : 'landing';
  });
  const [activeStudentId, setActiveStudentId] = useState(() => {
    const session = state.session;
    const id = session?.kind === 'student' ? session.id : session?.kind === 'agent' ? session.studentId : null;
    return id && state.students?.[id] ? id : null;
  });
  const [activeAgentRoleId, setActiveAgentRoleId] = useState(() => state.session?.kind === 'agent' && ROLES_BY_ID[state.session.roleId] ? state.session.roleId : null);
  const [teacherSubView, setTeacherSubView] = useState({ kind: 'overview' });
  const { toast, ToastView } = useToast();

  useEffect(() => { startSandBackground(); }, []);
  useEffect(() => SandPlatform.mount(renderState), []);
  useEffect(() => {
    if (!SandPlatform.ready) return;
    if (view === 'teacher' && SandPlatform.auth.role !== 'teacher') setView('landing');
    if (view === 'student' && !state.students[activeStudentId]) { setActiveStudentId(null); setView('landing'); }
    if (view === 'agent' && activeStudentId && !state.students[activeStudentId]) { setActiveStudentId(null); setView('landing'); }
  }, [state.students, view, activeStudentId]);

  useEffect(() => {
    setState(s => {
      let session = null;
      if (view === 'student' && activeStudentId) session = { kind: 'student', id: activeStudentId };
      else if (view === 'agent' && activeAgentRoleId) session = { kind: 'agent', roleId: activeAgentRoleId, studentId: activeStudentId };
      else if (view === 'teacher') session = { kind: 'teacher' };
      else if (view === 'showcase') session = { kind: 'showcase' };
      return { ...s, session };
    });
  }, [view, activeStudentId, activeAgentRoleId]);

  function patchStudent(id, updater) {
    setState(s => ({
      ...s,
      students: { ...s.students, [id]: updater(s.students[id]) },
    }));
  }

  function appendFreeChat(roleId, msg) {
    setState(s => {
      const list = [...(s.anonChats?.[roleId] || []), msg];
      return { ...s, anonChats: { ...(s.anonChats || {}), [roleId]: list } };
    });
  }
  function appendStudentFreeChat(studentId, roleId, msg) {
    setState(s => {
      const stu = s.students[studentId];
      const chats = [...(stu.freeChats || []), { ...msg, roleId }];
      return { ...s, students: { ...s.students, [studentId]: { ...stu, freeChats: chats } } };
    });
  }
  function clearAnonChatFor(roleId) {
    setState(s => ({ ...s, anonChats: { ...(s.anonChats || {}), [roleId]: [] } }));
  }
  function clearStudentChatFor(studentId, roleId) {
    setState(s => {
      const stu = s.students[studentId];
      return { ...s, students: { ...s.students, [studentId]: { ...stu, freeChats: (stu.freeChats || []).filter(m => m.roleId !== roleId) } } };
    });
  }

  async function handleLogout() {
    try { await SandPlatform.logout(); }
    catch (e) { toast('暂未退出：' + e.message, 'error'); return; }
    setView('landing');
    setActiveStudentId(null);
    setActiveAgentRoleId(null);
    setTeacherSubView({ kind: 'overview' });
  }

  if (!SandPlatform.ready) return <div className="startup-screen" role="status"><span>一沙一世界</span><p>正在连接学习记录服务…</p></div>;

  return (
    <Fragment>
      <PlatformStatus />
      <ExhibitionControls state={state} view={view} onHome={() => setView('landing')} onNextVisitor={async () => { await handleLogout(); if (SandPlatform.error || SandPlatform.auth.role !== 'visitor') throw new Error('请先完成保存后再退出'); }} onOpenShowcase={() => setView('showcase')} onAgent={roleId => { setActiveAgentRoleId(roleId); setView('agent'); }} />
      {view === 'landing' && (
        <LandingView
          onStudent={(id) => { setActiveStudentId(id); setView('student'); toast('欢迎进入一沙一世界 🌾', 'success'); }}
          onAgent={(roleId) => { setActiveAgentRoleId(roleId); setView('agent'); }}
          onTeacher={() => { setView('teacher'); toast('教师工作台已开启', 'success'); }}
          onShowcase={() => { setView('showcase'); }}
          state={state}
          onReset={() => { const fresh = resetState(); setState(fresh); toast('已重置示例数据', 'success'); }}
        />
      )}
      {view === 'student' && activeStudentId && state.students[activeStudentId] && (
        <StudentView
          student={state.students[activeStudentId]}
          patchStudent={(updater) => patchStudent(activeStudentId, updater)}
          knowledge={state.knowledge || {}}
          agentOverrides={state.agentOverrides || {}}
          weaponOverrides={state.weaponOverrides || {}}
          onAgent={(roleId) => { setActiveAgentRoleId(roleId); setView('agent'); }}
          onLogout={handleLogout}
          toast={toast}
        />
      )}
      {view === 'agent' && activeAgentRoleId && (
        <AgentChatView
          state={state}
          roleId={activeAgentRoleId}
          studentId={activeStudentId}
          student={activeStudentId ? state.students[activeStudentId] : null}
          onBack={() => activeStudentId ? setView('student') : setView('landing')}
          onSwitchAgent={(rid) => setActiveAgentRoleId(rid)}
          appendAnon={(msg) => appendFreeChat(activeAgentRoleId, msg)}
          appendStudent={(msg) => activeStudentId && appendStudentFreeChat(activeStudentId, activeAgentRoleId, msg)}
          clearAnonChat={() => clearAnonChatFor(activeAgentRoleId)}
          clearStudentChat={() => activeStudentId && clearStudentChatFor(activeStudentId, activeAgentRoleId)}
          onLogout={handleLogout}
          toast={toast}
        />
      )}
      {view === 'teacher' && (
        <TeacherView
          state={state}
          setState={setState}
          subView={teacherSubView}
          setSubView={setTeacherSubView}
          onLogout={handleLogout}
          toast={toast}
        />
      )}
      {view === 'showcase' && (
        <ShowcaseView
          state={state}
          onClose={handleLogout}
        />
      )}
      {ToastView}
    </Fragment>
  );
}

/* =====================================================================
   1) Landing — 沙盘登录页
   ===================================================================== */

function LegacyLandingView({ onStudent, onAgent, onTeacher, onShowcase, state, onReset }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [teacherOpen, setTeacherOpen] = useState(false);

  return (
    <div className="content-max px-5 sm:px-8 pt-6 pb-16">
      <TopBar
        left={<ProjectLogo />}
        right={
          <Fragment>
            <button className="sand-button sand-button-ghost text-xs sm:text-sm px-3 py-1.5" onClick={() => setHelpOpen(true)}>📖 这是什么</button>
            <button className="sand-button sand-button-ghost text-xs sm:text-sm px-3 py-1.5" onClick={() => setSignInOpen(true)}>🪪 学生登录</button>
            <button className="sand-button sand-button-ghost text-xs sm:text-sm px-3 py-1.5" onClick={() => setTeacherOpen(true)}>🧑‍🏫 教师</button>
            <button className="sand-button sand-button-ghost text-xs sm:text-sm px-3 py-1.5" onClick={onShowcase}>🌾 成果展</button>
          </Fragment>
        }
      />

      {/* Hero */}
      <div className="paper-card p-7 sm:p-10 mt-6 relative overflow-hidden fade-up">
        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8">
            <div className="text-xs text-sand-700 tracking-widest mb-2">{PROJECT.schoolName} · {PROJECT.grade} · 学习力跨学科项目</div>
            <h1 className="font-display text-4xl sm:text-6xl text-sand-900 title-shadow leading-tight">{PROJECT.name}</h1>
            <div className="font-display text-lg sm:text-2xl text-sand-800 mt-1">{PROJECT.motto}</div>
            <p className="mt-4 text-sand-800 leading-relaxed max-w-2xl">
              九位项目智能体已经在沙的另一边等你了——
              <b>不用登录，点哪个角色头像，就直接和他聊。</b>
              想保存学习记录？右上角"学生登录"留下你的名字，记录会随你走，老师可以看到。
            </p>
            <div className="mt-5 flex flex-wrap gap-2 fade-up-stagger">
              <span className="chip gold">🪪 免登录可对话</span>
              <span className="chip green">📿 登录保存记录</span>
              <span className="chip blue">📚 老师可补充知识</span>
              <span className="chip terra">🌾 嘉宾通道成果展</span>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-center justify-center">
            <div className="relative w-full">
              <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle,#fff5dc 0%,transparent 65%)' }} />
              <div className="relative">
                <RoleCharacter role={ROLES[0]} size={200} />
              </div>
            </div>
          </div>
        </div>
        {/* 装饰 */}
        <div className="absolute -right-12 -bottom-12 w-56 h-56 rounded-full opacity-40 pointer-events-none"
             style={{ background: 'radial-gradient(circle,#fff5dc 0%,transparent 70%)' }}/>
      </div>

      {/* 9 个智能体角色卡 */}
      <div className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-2xl text-sand-900">九位项目智能体</div>
            <div className="text-xs text-sand-700">点头像 · 直接对话 · 一沙一世界的九重身份</div>
          </div>
          <div className="text-xs text-sand-700 hidden sm:block">已激活 9 / 9</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-4 sm:gap-5 fade-up-stagger">
          {ROLES.map(r => {
            const v = VOICES[r.id];
            const kbCount = (state.knowledge?.[r.id] || []).length;
            return (
              <button key={r.id}
                      className="agent-card text-left"
                      onClick={() => onAgent(r.id)}>
                <div className="agent-glow" />
                <div className="relative flex items-center justify-center mb-2">
                  <RoleCharacter role={r} size={170} />
                </div>
                <div className="relative w-full text-center">
                  <div className="font-display text-xl text-sand-900 leading-tight">{r.roleName}</div>
                  <div className="text-xs text-sand-700 mt-0.5">{r.teamName} · {r.className}</div>
                  <div className="text-[12px] text-sand-800 italic mt-1.5 line-clamp-2 min-h-[2.4em]">「{v?.catchphrase || r.tagline}」</div>
                  <div className="mt-2.5 flex items-center justify-center gap-1.5 flex-wrap">
                    <span className="chip">💬 自由对话</span>
                    {kbCount > 0 && <span className="chip green">📚 {kbCount} 篇资料</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 五阶段说明 */}
      <div className="paper-card p-5 mt-7 fade-up">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-xl text-sand-900">学的五个地层</div>
            <div className="text-xs text-sand-700 mt-0.5">学生登录后，可以走完 学-问-思-辨-行 的完整学习闭环。</div>
          </div>
          <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => setSignInOpen(true)}>开始我的学习之旅 →</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
          {STAGES.map(s => (
            <div key={s.key} className="bg-white/55 rounded-xl p-3 text-center">
              <div className="text-2xl">{s.icon}</div>
              <div className="font-display text-sand-900 mt-0.5">{s.title} · {s.name.split('的')[1]?.slice(0,2) || s.title}</div>
              <div className="text-[11px] text-sand-700 mt-0.5">{s.intro.split('，')[0]}</div>
            </div>
          ))}
        </div>
      </div>

      <Modal open={helpOpen} onClose={() => setHelpOpen(false)} maxW="max-w-3xl">
        <HelpIntro onClose={() => setHelpOpen(false)} onShowcase={onShowcase} />
      </Modal>

      <Modal open={signInOpen} onClose={() => setSignInOpen(false)} maxW="max-w-xl">
        <StudentSignIn state={state} onSignedIn={(id) => { setSignInOpen(false); onStudent(id); }} onClose={() => setSignInOpen(false)} />
      </Modal>

      <Modal open={teacherOpen} onClose={() => setTeacherOpen(false)} maxW="max-w-md">
        <TeacherSignIn onSignedIn={() => { setTeacherOpen(false); onTeacher(); }} onClose={() => setTeacherOpen(false)} />
      </Modal>
    </div>
  );
}

/* ---------- 学生登录 (放进 modal) ---------- */
function StudentSignIn(props) { return <PlatformStudentSignIn {...props}/>; }
function LegacyStudentSignIn({ state, onSignedIn, onClose }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  function go() {
    const role = ROLES_BY_CODE[code.toUpperCase().trim()];
    if (!role) { setError('班级码无效。试一下 WU1 - WU8 或 ART。'); return; }
    if (!name.trim()) { setError('请输入你的姓名，用来找到这台设备上的学习记录。'); return; }
    setError('');

    const existing = Object.values(state.students).find(s => s.name === name.trim() && s.class === role.className);
    if (existing) { onSignedIn(existing.id); return; }

    const id = 'self-' + role.classCode + '-' + Date.now();
    const stageOrder = ['learn','ask','think','debate','act'];
    state.students[id] = {
      id, name: name.trim(), class: role.className, roleId: role.id, team: '自由小组',
      currentStage: 'learn',
      stages: stageOrder.reduce((acc, key) => {
        acc[key] = {
          status: key === 'learn' ? 'in-progress' : 'locked',
          conversation: [], submission: { text: '', reflection: '', media: [] },
          aiScore: null, teacherScore: null, teacherNote: '',
        };
        return acc;
      }, {}),
      portfolioReflection: '',
      createdAt: new Date().toISOString().slice(0,10),
      freeChats: [],
    };
    saveState(state);
    onSignedIn(id);
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs text-sand-700 tracking-widest">可选 · 学生登录</div>
          <div className="font-display text-2xl text-sand-900">留下名字，继续你的学习旅程</div>
        </div>
        <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onClose}>关闭</button>
      </div>
      <p className="text-sm text-sand-700 leading-relaxed mb-3">
        登录后，对话、提交和反思会保存在这台设备的当前浏览器中，再次输入相同班级码和姓名就能继续。记录暂不跨设备同步。<br/>
        <b>不登录也可以直接和智能体聊</b>，访客对话同样会暂存于当前浏览器。
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-sand-700">班级码</label>
          <input
            className="w-full mt-1 px-4 py-3 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 placeholder-sand-500/70 font-medium tracking-widest uppercase"
            placeholder="WU1 / WU2 / ... / ART"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {ROLES_BY_CODE[code.toUpperCase().trim()] && (
            <div className="mt-2 chip green">即将进入：{ROLES_BY_CODE[code.toUpperCase().trim()].fullName}</div>
          )}
        </div>
        <div>
          <label className="text-xs text-sand-700">你的姓名</label>
          <input
            className="w-full mt-1 px-4 py-3 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 placeholder-sand-500/70"
            placeholder="例如：陆星瑶"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && go()}
          />
        </div>
        {error && <div className="text-sm text-terra-500">{error}</div>}
        <button className="sand-button w-full" onClick={go}>登录并走进沙世界 →</button>

        <div className="divider-sand" />
        <div className="text-xs text-sand-700">
          <div className="mb-1.5">📌 班级码 → 项目角色</div>
          <div className="grid grid-cols-3 gap-1.5">
            {ROLES.map(r => (
              <button key={r.id}
                      className="text-xs px-2 py-1 rounded-lg bg-sand-50/80 border border-sand-300/40 hover:bg-sand-100"
                      onClick={() => setCode(r.classCode)}>
                <div className="font-mono text-sand-800">{r.classCode}</div>
                <div className="text-[10px] text-sand-700 truncate">{r.teamName}</div>
              </button>
            ))}
          </div>
          <div className="divider-sand" />
          <div className="mb-1.5">🌟 或一键以演示学生身份进入</div>
          <div className="grid grid-cols-1 gap-1.5 max-h-[180px] overflow-auto pr-1">
            {Object.values(state.students).slice(0, 8).map(s => {
              const r = ROLES_BY_ID[s.roleId];
              return (
                <button key={s.id} className="paper-card p-2 flex items-center gap-2 hover:translate-x-1 transition text-left text-xs"
                        onClick={() => onSignedIn(s.id)}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm shadow shrink-0"
                       style={{ background: r.bg }}>{r.emoji}</div>
                  <div className="flex-1 leading-tight min-w-0">
                    <div className="font-medium text-sand-900 truncate">{s.name} · {s.class}</div>
                    <div className="text-[11px] text-sand-700 truncate">{r.teamName}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeacherSignIn(props) { return <PlatformTeacherSignIn {...props}/>; }
function LegacyTeacherSignIn({ onSignedIn, onClose }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  function go() {
    if (code.trim().toUpperCase() !== 'TEACH') { setError('教师码无效，试试 TEACH'); return; }
    onSignedIn();
  }
  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs text-sand-700 tracking-widest">教师入口</div>
          <div className="font-display text-2xl text-sand-900">教师工作台</div>
        </div>
        <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onClose}>关闭</button>
      </div>
      <p className="text-sm text-sand-700 leading-relaxed mb-3">
        进入工作台后可以：调整 9 位角色和 8 把学习工具、补充<b>课程知识库</b>（文档/图片）、管理本机成果展与 AI 设置。当前修改保存在这台设备的浏览器中。
      </p>
      <div className="space-y-2">
        <input
          className="w-full mt-1 px-4 py-3 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 uppercase tracking-widest"
          placeholder="教师码 TEACH"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
        {error && <div className="text-sm text-terra-500">{error}</div>}
        <button className="sand-button w-full" onClick={go}>进入工作台 →</button>
      </div>
    </div>
  );
}

function HelpIntro({ onClose, onShowcase }) {
  const steps = [
    { icon: '🪪', title: '学生：输入班级码 + 姓名', desc: '班级码已和角色绑定。比如五1班是 WU1 → 沙盘组｜地质沙盘工程师。' },
    { icon: '🗺️', title: '走进沙盘地图首页', desc: '看到自己的角色、任务、五阶段地层、九重沙之世界的所有项目组。' },
    { icon: '🌱', title: '逐关解锁「学-问-思-辨-行」', desc: 'AI 引导式辅导，不代写答案。完成对话、提交内容和反思后进入下一关，也可以上传作品证据。' },
    { icon: '🪣', title: '上传你的全媒体证据', desc: '照片、视频、音频、文档都可以——做沙盘的过程比成品更珍贵。' },
    { icon: '📿', title: '成长沙瓶', desc: '每完成一关，沙瓶就多一层。完整看到你的"一沙一世界"之旅。' },
    { icon: '🎓', title: '学习反馈 + 项目成果展', desc: '提交阶段任务后由教师审核评分；经过授权的精选作品可以发布到公开展厅。' },
  ];
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-sand-700 tracking-widest">关于一沙一世界 · MVP</div>
          <div className="font-display text-3xl text-sand-900">这是什么？</div>
        </div>
        <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onClose}>关闭</button>
      </div>
      <p className="text-sand-800 leading-relaxed">
        这是 <b>八小五年级跨学科项目「一沙一世界」</b>的学习智能体平台 MVP。
        这里有 <b>9 位角色智能体和 8 把 AI 学习工具</b>，让每个班级的孩子带着自己的"项目身份"——
        沙盘工程师、夯土工程师、招商策划师、声音戏剧导演……一步步走完五阶段学习闭环。
      </p>
      <div className="divider-sand" />
      <div className="grid sm:grid-cols-2 gap-3">
        {steps.map((s, i) => (
          <div key={i} className="paper-card p-3 flex items-start gap-3">
            <div className="text-2xl shrink-0">{s.icon}</div>
            <div>
              <div className="font-display text-base text-sand-900">{i+1}. {s.title}</div>
              <div className="text-xs text-sand-700 mt-0.5 leading-relaxed">{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="divider-sand" />
      <div className="text-sand-800 text-sm leading-relaxed space-y-1.5">
        <div>🪶 <b>AI 原则</b>：引导式不代写，任务内自由问答，课程资料优先；建议评分用于学习参考。</div>
        <div>💾 <b>记录保存</b>：登录后的记录和附件保存在本机服务，同一服务下可跨设备继续。访客对话仅临时保留；公开作品由教师审核并确认授权。</div>
        <div>🛡 <b>数据隐私</b>：成果展默认仅展示姓名/化名和作品本身，不暴露生日、住址、联系方式。</div>
        <div>📱 <b>使用场景</b>：以平板课堂为主，UI 为触屏优化。</div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <div className="text-xs text-sand-700">想看孩子的作品长什么样？</div>
        <button className="sand-button" onClick={() => { onClose(); onShowcase(); }}>👉 进入成果展看看</button>
      </div>
    </div>
  );
}

/* =====================================================================
   2) AgentChatView — 自由对话视图 (匿名 or 学生)
   ===================================================================== */

function AgentChatView({ state, roleId, studentId, student, onBack, onSwitchAgent, appendAnon, appendStudent, clearAnonChat, clearStudentChat, onLogout, toast }) {
  const role = ROLES_BY_ID[roleId];
  const voice = VOICES[roleId];
  const knowledge = state.knowledge?.[roleId] || [];

  // 当前对话流: 学生 → student.freeChats(过滤这个 roleId); 匿名 → state.anonChats[roleId]
  const messages = useMemo(() => {
    if (student) return (student.freeChats || []).filter(m => m.roleId === roleId);
    return state.anonChats?.[roleId] || [];
  }, [student, state.anonChats, roleId]);

  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const sendingRef = useRef(false);
  const [showKB, setShowKB] = useState(false);
  const [equippedWeaponId, setEquippedWeaponId] = useState(null);    // 装备的武器
  const [pendingImage, setPendingImage] = useState(null);            // { prompt } - 正在生图占位
  const [kbDetail, setKbDetail] = useState(null);                    // 点开的资料详情
  const [showSwitch, setShowSwitch] = useState(false);               // 换角色 modal
  const [mobilePanel, setMobilePanel] = useState(null);

  // 合并教师 override 后的 voice/mission (左侧展示用)
  const agentOv = (state.agentOverrides || {})[roleId] || {};
  const voiceEff = {
    ...voice,
    nickname:    agentOv.nickname    ?? voice.nickname,
    catchphrase: agentOv.catchphrase ?? voice.catchphrase,
    voice:       agentOv.voice       ?? voice.voice,
  };
  const missionEff = agentOv.mission ?? role.mission;
  const chatRef = useRef();

  const equippedWeapon = equippedWeaponId ? WEAPONS_BY_ID[equippedWeaponId] : null;
  // 切换角色时,卸下武器(每个角色独立武器槽)
  useEffect(() => { setEquippedWeaponId(null); setPendingImage(null); }, [roleId]);

  /* 行内生图: 调用 /api/image, 成功后把图片消息推入对话流 */
  async function generateImageInline(prompt) {
    if (!prompt?.trim()) { toast('请先有图片提示词', 'error'); return; }
    setPendingImage({ prompt });
    const generation = SandPlatform.generation;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 120000);   // 120s 超时
    try {
      const r = await fetch('/api/image', {
        method: 'POST',
        credentials: isExhibitionKiosk() ? 'omit' : 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
        signal: ctrl.signal,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'HTTP ' + r.status);
      if (generation !== SandPlatform.generation) return;
      const imgMsg = { role: 'ai', kind: 'image', imageUrl: data.url, imagePrompt: prompt, media: data.media, temporary: data.temporary, storageWarning: data.storageWarning, at: nowStr() };
      if (student) appendStudent(imgMsg);
      else appendAnon(imgMsg);
      toast(data.storageWarning ? '图片已生成但保存失败，请下载原图：' + data.storageWarning : '🎨 万相已生成', data.storageWarning ? 'error' : 'success');
    } catch (e) {
      if (generation !== SandPlatform.generation) return;
      const friendly = e.name === 'AbortError'
        ? '万相生图超时(120秒)。再试一次?'
        : `万相生图失败: ${e.message}`;
      const errMsg = { role: 'ai', kind: 'text', text: `⚠️ ${friendly}`, at: nowStr() };
      if (student) appendStudent(errMsg);
      else appendAnon(errMsg);
      toast(friendly, 'error');
    } finally {
      clearTimeout(timer);
      setPendingImage(null);
    }
  }

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, thinking]);

  async function send(overrideText) {
    // 注意: 按钮 onClick={() => send()} / 重试时 send(text)
    // 如果误传了 event 对象, 当 undefined 处理
    const isRetry = typeof overrideText === 'string';
    const text = (isRetry ? overrideText : draft).trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true;
    const generation = SandPlatform.generation;
    const userMsg = { role: 'student', text, at: nowStr(), name: student?.name || '访客' };
    if (student) appendStudent(userMsg);
    else appendAnon(userMsg);

    if (!isRetry) setDraft('');
    setThinking(true);
    try {
      if (AI_CONFIG.provider === 'mock') {
        await new Promise(r => setTimeout(r, 700 + Math.random() * 500));
      }
      const resp = await aiChatAsync({
        mode: 'free',
        roleId, weaponId: equippedWeaponId,
        message: text,
        history: messages,
        knowledge, student,
        agentOverrides: state.agentOverrides || {},
        weaponOverrides: state.weaponOverrides || {},
      });
      const aiMsg = {
        role: 'ai', text: resp.text, at: nowStr(), source: resp.source,
        failed: resp.source === 'error' || resp.source === 'mock-fallback',
        retryText: (resp.source === 'error' || resp.source === 'mock-fallback') ? text : undefined,
      };
      if (generation !== SandPlatform.generation) return;
      if (student) appendStudent(aiMsg);
      else appendAnon(aiMsg);
    } catch (e) {
      console.error(e);
      toast('AI 出错: ' + e.message, 'error');
    } finally {
      sendingRef.current = false;
      setThinking(false);
    }
  }

  // 重试一条失败的消息
  function retryFailedMessage(retryText) {
    send(retryText);
  }

  return (
    <Fragment>
      <TopBar
        left={<ProjectLogo small />}
        right={
          <Fragment>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={onBack}>← 返回</button>
            {!student && (
              <span className="chip">访客体验 · 对话保存在当前浏览器</span>
            )}
            {student && (
              <Fragment>
                <Chip variant="green">{student.name} · {student.class}</Chip>
                <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={onLogout}>退出</button>
              </Fragment>
            )}
          </Fragment>
        }
      />
      <div className="content-max px-4 sm:px-8 pb-10 chat-studio">
        <div className="mobile-chat-controls">
          <button className="sand-button sand-button-ghost" onClick={() => setShowSwitch(true)}><ExhibitionIcon name="user" size={16}/>更换导师</button>
          <button className="sand-button sand-button-ghost" aria-expanded={mobilePanel === 'tools'} onClick={() => setMobilePanel(mobilePanel === 'tools' ? null : 'tools')}><ExhibitionIcon name="spark" size={16}/>{mobilePanel === 'tools' ? '收起工具' : '选择学习工具'}{equippedWeapon ? ` · ${equippedWeapon.name}` : ''}</button>
          <button className="sand-button sand-button-ghost" aria-expanded={mobilePanel === 'identity'} onClick={() => setMobilePanel(mobilePanel === 'identity' ? null : 'identity')}>导师介绍</button>
        </div>
        <div className="chat-layout mt-2">
          {/* 左列: 当前角色的身份卡 + 任务帮助 */}
          <div className={`chat-identity ${mobilePanel === 'identity' ? 'open' : ''}`}>
            <RoleIdentityPanel
              role={role}
              voiceEff={voiceEff}
              missionEff={missionEff}
              knowledgeCount={knowledge.length}
              onAskExample={(q) => setDraft(q)}
              onOpenKB={() => setShowKB(true)}
              onSwitchRole={() => setShowSwitch(true)}
              roleId={roleId}
              onSwitchAgent={onSwitchAgent}
            />
          </div>

          {/* 中列: 对话 */}
          <div className="chat-main">
            <div className="paper-card p-0 overflow-hidden flex flex-col"
                 style={{ height: 'calc(100vh - 140px)', minHeight: 520 }}>
              <div className="chat-titlebar px-5 pt-4 pb-3 border-b border-sand-300/30 flex items-center justify-between flex-wrap gap-2 shrink-0">
                <div className="flex items-center gap-2">
                  <AIAvatar role={role} size={40} thinking={thinking} />
                  <div className="leading-tight">
                    <div className="text-sand-900 font-medium text-sm">{voiceEff.nickname} <span className="text-xs text-sand-700">（{role.roleName}）</span></div>
                    <div className="text-xs text-sand-700">{thinking ? '正在思考…' : voiceEff.voice}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {knowledge.length > 0 && (
                    <button className="chip" onClick={() => setShowKB(true)} title="查看老师上传的课程资料">📚 {knowledge.length} 篇资料</button>
                  )}
                  {equippedWeapon && (
                    <div className="equipped-bar">
                      <WeaponIcon weapon={equippedWeapon} size={24} equipped />
                      <span>已装备 <b>{equippedWeapon.name}</b></span>
                      <button className="text-xs text-terra-500 hover:underline ml-1" onClick={() => setEquippedWeaponId(null)}>×</button>
                    </div>
                  )}
                  {messages.length > 0 && (
                    <button className="chip terra" onClick={() => {
                      if (!confirm(`确定清空所有和 ${voiceEff.nickname} 的对话吗?`)) return;
                      if (student) clearStudentChat();
                      else clearAnonChat();
                      toast('对话已清空', 'success');
                    }} title="清空对话">🗑 重置</button>
                  )}
                  {student && <Chip variant="green">{student.name}</Chip>}
                  {!student && <Chip variant="terra">匿名</Chip>}
                </div>
              </div>

              <div ref={chatRef} role="log" aria-label="导师对话记录" aria-live="polite" className="chat-history flex-1 overflow-auto px-5 py-4 flex flex-col gap-2.5">
                <div className="chat-bubble system">
                  你正在和 <b>{voice.nickname}</b> 自由对话。{voice.voice}。
                </div>
                {messages.length === 0 && (
                  <div className="chat-bubble ai">
                    {voice.greeting}
                  </div>
                )}
                {messages.map((m, i) => {
                  // 图片消息
                  if (m.kind === 'image') {
                    return (
                      <div key={i} className="chat-bubble ai chat-bubble-image">
                        <img src={m.imageUrl} alt="万相生成" className="rounded-lg max-w-full"
                             onClick={() => window.open(m.imageUrl, '_blank')}
                             style={{ cursor: 'zoom-in' }} />
                        <div className="image-caption">"{m.imagePrompt}"</div>
                        <div className="text-[11px] text-sand-700 mt-1">真实 AI 生成 · 教学插图{m.temporary ? ' · 临时预览，请及时下载' : ' · 已保存到本机服务'}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <a href={m.imageUrl} download="sand-world.png" className="text-[11px] text-sand-700 hover:underline">📥 保存</a>
                          <a href={m.imageUrl} target="_blank" rel="noreferrer" className="text-[11px] text-sand-700 hover:underline">🔍 大图</a>
                        </div>
                      </div>
                    );
                  }
                  // 文本消息 (含可能的 🎨 图片提示词)
                  const showInlineImgAction = (
                    m.role === 'ai' &&
                    equippedWeapon?.supportsImageGen &&
                    /🎨[^\n]*\n[^\n]+/.test(m.text || '')
                  );
                  return (
                    <div key={i} className={`chat-bubble ${m.role} ${m.failed ? 'chat-bubble-failed' : ''}`}>
                      {m.source && <div className="text-[10px] mb-1 opacity-70">{m.source === 'proxy' || m.source === 'qwen' ? '真实 AI 回复 · 请核对事实' : m.source === 'error' ? '真实 AI 未成功' : '本地教学模拟 · 非真实 AI 回复'}</div>}
                      {m.role === 'ai' ? renderMarkdown(m.text) : m.text}
                      {showInlineImgAction && (
                        <InlineImageAction
                          rawText={m.text}
                          onGenerate={generateImageInline}
                          isLoading={!!pendingImage}
                        />
                      )}
                      {m.failed && m.retryText && (
                        <button
                          className="mt-3 sand-button text-xs px-3 py-1.5"
                          onClick={() => retryFailedMessage(m.retryText)}
                          disabled={thinking}
                        >
                          🔄 重新让 AI 试一次
                        </button>
                      )}
                    </div>
                  );
                })}
                {thinking && (
                  <div className="chat-bubble ai inline-flex items-center gap-2">
                    <span>{voiceEff.nickname} 正在想</span>
                    <span className="typing-dots"><span/><span/><span/></span>
                  </div>
                )}
                {pendingImage && (
                  <div className="chat-bubble ai chat-bubble-image-loading">
                    <div className="flex items-center gap-2">
                      <span>🎨 通义万相正在画</span>
                      <span className="typing-dots"><span/><span/><span/></span>
                    </div>
                    <div className="image-caption">"{pendingImage.prompt}"</div>
                  </div>
                )}
              </div>

              <div className="chat-composer border-t border-sand-300/30 px-4 py-3">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(equippedWeapon ? equippedWeapon.starters : freeChatStarters(role)).map((p, i) => (
                    <button key={i} className="chip" onClick={() => setDraft(p)}>{p}</button>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2}
                    aria-label="给导师的消息"
                    className="flex-1 px-3 py-2.5 rounded-xl bg-white/80 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
                    placeholder={equippedWeapon
                      ? `把要给 ${equippedWeapon.name} 处理的内容贴这里…`
                      : `和 ${voice.nickname} 说说话…`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
                  />
                  <button className="sand-button" onClick={() => send()} disabled={!draft.trim() || thinking}>发送 <ExhibitionIcon name="arrow" size={17}/></button>
                </div>
                <div className="text-[11px] text-sand-700 mt-1">
                  ⌘/Ctrl + Enter 发送
                  {equippedWeapon ? <span> · 武器: <b>{equippedWeapon.name}</b> ({equippedWeapon.aliasMethod})</span> : ' · 自由对话模式'}
                  · {student ? `已登录为 ${student.name}` : '匿名访客'}
                </div>
              </div>
            </div>
          </div>

          {/* 右列: 8 把武器 */}
          <div className={`chat-tools ${mobilePanel === 'tools' ? 'open' : ''}`}>
            <div className="paper-card p-4 lg:sticky lg:top-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="exhibit-eyebrow">THINKING TOOLKIT</div>
                  <div className="font-display text-base text-sand-900 mt-1">选择一种学习方法</div>
                  <div className="text-[11px] text-sand-700 mt-1">八种工具，让想法更进一步</div>
                </div>
                <button className="mobile-tools-close sand-button sand-button-ghost" aria-label="关闭学习工具" onClick={() => setMobilePanel(null)}><ExhibitionIcon name="close" size={18}/></button>
                {equippedWeapon && (
                  <button className="text-[11px] text-terra-500 hover:underline" onClick={() => setEquippedWeaponId(null)}>
                    取消选择
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {WEAPONS.map(w => {
                  const isEq = equippedWeaponId === w.id;
                  return (
                    <button key={w.id}
                            className={`weapon-card ${isEq ? 'equipped' : ''}`}
                            aria-pressed={isEq}
                            onClick={() => { setEquippedWeaponId(isEq ? null : w.id); setMobilePanel(null); }}
                            title={`${w.aliasMethod} · ${w.problemSolved}`}>
                      {isEq && <div className="equipped-badge">已装备</div>}
                      <WeaponIcon weapon={w} size={56} equipped={isEq} />
                      <div className="weapon-name">{w.name}</div>
                      <div className="weapon-tagline">{w.tagline}</div>
                    </button>
                  );
                })}
              </div>
              <div className="divider-sand my-3" />
              {!equippedWeapon ? (
                <div className="text-[11px] text-sand-700 leading-relaxed">
                  选择工具后，导师会用对应的学习方法回应你。<br/>
                  例：<b>{role.roleName}</b> + <b>零件分离机</b> = 本角色的口吻 + 分步拆解法。
                </div>
              ) : (
                <WeaponUsageGuide weapon={equippedWeapon} role={role} onTry={(text) => setDraft(text)} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 知识库 modal (列表) */}
      <Modal open={showKB} onClose={() => setShowKB(false)} maxW="max-w-2xl">
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-sand-700 tracking-widest">老师上传的参考素材</div>
              <div className="font-display text-2xl text-sand-900">📚 {role.roleName} 的知识库</div>
              <div className="text-xs text-sand-700 mt-0.5">共 {knowledge.length} 篇。点开任意一篇查看完整内容。AI 主要靠自己的思考,这些只是参考。</div>
            </div>
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => setShowKB(false)}>关闭</button>
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-auto pr-1">
            {knowledge.map(k => (
              <button key={k.id}
                      onClick={() => setKbDetail(k)}
                      className="w-full text-left bg-white/55 hover:bg-white/85 rounded-xl p-3 border border-sand-300/30 hover:border-sand-400/60 transition">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] tracking-widest text-sand-700 bg-sand-200/60 rounded px-1.5 py-0.5">{k.tag || '资料'}</span>
                  <span className="text-sm font-medium text-sand-900">{k.title}</span>
                  <span className="ml-auto text-[10px] text-sand-700">点开 →</span>
                </div>
                <div className="text-xs text-sand-700 mt-1.5 leading-relaxed line-clamp-2">{k.content}</div>
                <div className="text-[10px] text-sand-700 mt-1.5">{k.addedBy} · {k.addedAt}{k.media ? ' · 带附件' : ''}</div>
              </button>
            ))}
            {knowledge.length === 0 && <div className="text-sm text-sand-700 py-6 text-center">老师还没有给这个智能体补充资料。</div>}
          </div>
        </div>
      </Modal>

      {/* 换角色 modal */}
      <Modal open={showSwitch} onClose={() => setShowSwitch(false)} maxW="max-w-4xl">
        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs text-sand-700 tracking-widest">银川八小 · 一沙一世界</div>
              <div className="font-display text-2xl text-sand-900">九位项目智能体</div>
              <div className="text-xs text-sand-700 mt-0.5">点头像 · 切换到那位老师 · 当前对话会被保留</div>
            </div>
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => setShowSwitch(false)}>关闭</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
            {ROLES.map(r => {
              const v = VOICES[r.id];
              const isCurr = r.id === roleId;
              return (
                <button key={r.id}
                        className={`agent-card text-left ${isCurr ? 'border-sand-500 bg-sand-100/70' : ''}`}
                        onClick={() => { if(!isCurr) onSwitchAgent(r.id); setShowSwitch(false); }}>
                  <div className="agent-glow" />
                  <div className="relative flex items-center justify-center mb-2">
                    <RoleCharacter role={r} size={130} animate={false} />
                  </div>
                  <div className="relative w-full text-center">
                    <div className="font-display text-lg text-sand-900 leading-tight">{r.roleName}</div>
                    <div className="text-[11px] text-sand-700 mt-0.5">{r.teamName} · 爱称 {v?.nickname}</div>
                    <div className="text-[11px] text-sand-800 italic mt-1 line-clamp-2 min-h-[2.4em]">「{v?.catchphrase || r.tagline}」</div>
                    {isCurr && <Chip variant="green">当前</Chip>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* 单篇资料详情 modal */}
      <Modal open={!!kbDetail} onClose={() => setKbDetail(null)} maxW="max-w-3xl">
        {kbDetail && (
          <div>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] tracking-widest text-sand-700 bg-sand-200/60 rounded px-1.5 py-0.5">{kbDetail.tag || '资料'}</span>
                  <span className="text-[10px] text-sand-700">{kbDetail.addedBy} · {kbDetail.addedAt}</span>
                </div>
                <div className="font-display text-2xl text-sand-900 leading-tight">{kbDetail.title}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => setKbDetail(null)}>← 返回列表</button>
              </div>
            </div>
            <div className="divider-sand" />
            <div className="text-sand-900 leading-relaxed whitespace-pre-wrap text-[15px]">{kbDetail.content}</div>
            {kbDetail.media && (
              <div className="mt-4">
                <div className="text-xs text-sand-700 tracking-widest mb-2">📎 附件</div>
                {kbDetail.media.kind === 'image' && kbDetail.media.data && (
                  <img src={kbDetail.media.data} alt={kbDetail.media.label} className="max-w-full rounded-xl border border-sand-300/40" />
                )}
                {kbDetail.media.kind === 'video' && kbDetail.media.data && (
                  <video src={kbDetail.media.data} controls className="max-w-full rounded-xl border border-sand-300/40" />
                )}
                {kbDetail.media.kind === 'audio' && kbDetail.media.data && (
                  <audio src={kbDetail.media.data} controls className="w-full" />
                )}
                {(!kbDetail.media.data || kbDetail.media.kind === 'doc') && (
                  <div className="bg-white/55 rounded-xl p-3 text-sm flex items-center gap-2">
                    <span className="text-2xl">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[kbDetail.media.kind] || '📎'}</span>
                    <span>{kbDetail.media.label}</span>
                  </div>
                )}
              </div>
            )}
            <div className="divider-sand" />
            <div className="text-xs text-sand-700 italic">
              💡 这只是给 AI 的参考素材。{role.roleName}回答你时,会用自己丰富的知识 + 这些参考一起想,不会把这段话照搬给你。
            </div>
          </div>
        )}
      </Modal>
    </Fragment>
  );
}

/* ---------- 武器使用说明 (装备时显示在右侧栏 8 把武器下方) ---------- */
function WeaponUsageGuide({ weapon, role, onTry }) {
  const g = weapon.usageGuide;
  if (!g) return null;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 -mb-1">
        <WeaponIcon weapon={weapon} size={22} />
        <div className="font-display text-sm text-sand-900">怎么用「{weapon.name}」</div>
      </div>

      <div>
        <div className="text-[10px] text-sand-700 tracking-widest mb-0.5">📖 怎么用</div>
        <div className="text-xs text-sand-900 leading-relaxed bg-white/55 rounded-lg p-2 border border-sand-300/30">
          {g.howToUse}
        </div>
      </div>

      <div>
        <div className="text-[10px] text-sand-700 tracking-widest mb-0.5 flex items-center justify-between">
          <span>✍️ 你可以这样输入 (点试一下)</span>
        </div>
        <button onClick={() => onTry(g.inputExample)}
                className="w-full text-left text-xs text-sand-900 leading-relaxed bg-amber-50/80 hover:bg-amber-100/80 rounded-lg p-2 border border-sand-400/40 transition italic">
          "{g.inputExample}"
          <div className="text-[10px] text-sand-700 mt-1 not-italic">→ 点一下,这句话会填到输入框</div>
        </button>
      </div>

      <div>
        <div className="text-[10px] text-sand-700 tracking-widest mb-0.5">💬 智能体会这样回</div>
        <div className="text-[11px] text-sand-900 leading-relaxed bg-white/70 rounded-lg p-2 border border-sand-300/30 max-h-[260px] overflow-auto">
          {renderMarkdown(g.outputExample)}
        </div>
      </div>

      {g.tips && (
        <div>
          <div className="text-[10px] text-sand-700 tracking-widest mb-0.5">💡 小提示</div>
          <div className="text-[11px] text-sand-800 leading-relaxed bg-sand-100/60 rounded-lg p-2 border border-sand-300/30">
            {g.tips}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- 左侧身份卡 + 任务帮助 + 试着问我 ---------- */
function RoleIdentityPanel({ role, voiceEff, missionEff, knowledgeCount, onAskExample, onOpenKB, onSwitchRole, roleId, onSwitchAgent }) {
  const v = VOICES[role.id];
  const helps = (v?.helpsWith) || [];
  const asks  = (v?.askExamples) || [];

  return (
    <div className="paper-card p-5 lg:sticky lg:top-4 fade-up chat-side-profile">
      {/* 头像 + 基本信息 */}
      <div className="flex flex-col items-center">
        <div className="relative">
          <RoleCharacter role={role} size={160} />
        </div>
        <div className="text-center mt-2">
          <div className="text-[11px] text-sand-700 tracking-widest">{role.teamName} · {role.className}</div>
          <div className="font-display text-xl text-sand-900 mt-0.5">{role.roleName}</div>
          <div className="text-xs text-sand-700 mt-0.5">爱称 · <b>{voiceEff.nickname}</b></div>
          <div className="text-sand-800 italic text-sm mt-2 leading-snug">「{voiceEff.catchphrase}」</div>
        </div>
      </div>

      <div className="divider-sand" />

      {/* 我是谁 */}
      <div>
        <div className="text-[11px] text-sand-700 tracking-widest mb-1.5">📜 关于我</div>
        <div className="text-sm text-sand-900 leading-relaxed">{missionEff}</div>
        {v?.likes?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {v.likes.map(t => <span key={t} className="chip">{t}</span>)}
          </div>
        )}
      </div>

      <div className="divider-sand" />

      {/* 我能帮你 */}
      {helps.length > 0 && (
        <div>
          <div className="text-[11px] text-sand-700 tracking-widest mb-1.5">✨ 我能帮你</div>
          <ul className="space-y-1.5">
            {helps.map((h, i) => (
              <li key={i} className="text-sm text-sand-900 flex items-start gap-1.5 leading-relaxed">
                <span className="text-sand-600 mt-0.5 shrink-0">·</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="divider-sand" />

      {/* 试着问我 — 点一下就填到输入框 */}
      {asks.length > 0 && (
        <div>
          <div className="text-[11px] text-sand-700 tracking-widest mb-1.5">💡 试着问我</div>
          <div className="space-y-1.5">
            {asks.map((q, i) => (
              <button key={i}
                      className="w-full text-left bg-white/55 hover:bg-white/85 rounded-lg p-2.5 border border-sand-300/30 hover:border-sand-400/60 transition text-xs text-sand-900 leading-snug"
                      onClick={() => onAskExample(q)}>
                <span className="text-sand-700 mr-1">→</span>{q}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-sand-700 mt-1.5 italic">点一下就填到输入框,可以改完再发。</div>
        </div>
      )}

      {knowledgeCount > 0 && (
        <Fragment>
          <div className="divider-sand" />
          <button className="w-full bg-white/55 hover:bg-white/85 rounded-lg p-2.5 border border-sand-300/30 hover:border-sand-400/60 transition text-sm text-sand-900 flex items-center justify-between"
                  onClick={onOpenKB}>
            <span>📚 老师上传的资料 ({knowledgeCount} 篇)</span>
            <span className="text-xs text-sand-700">查看 →</span>
          </button>
        </Fragment>
      )}

      <div className="divider-sand" />

      <button className="w-full sand-button sand-button-ghost text-sm" onClick={onSwitchRole}>
        🔄 换一位智能体聊
      </button>
    </div>
  );
}

/* ---------- 行内"让万相画出来" — 嵌入到 AI 文本气泡尾部 ---------- */
function InlineImageAction({ rawText, onGenerate, isLoading }) {
  const m = rawText.match(/🎨[^\n]*\n([^\n]+)/);
  const initial = m ? m[1].trim() : '';
  const [edited, setEdited] = useState(initial);
  useEffect(() => { setEdited(initial); }, [initial]);

  return (
    <div className="mt-3 pt-3 border-t border-sand-300/40">
      <div className="text-[10px] text-sand-700 tracking-widest mb-1">🎨 图片提示词 (可修改)</div>
      <textarea
        rows={2}
        className="w-full text-xs px-2.5 py-1.5 rounded-lg bg-white/80 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
        value={edited}
        onChange={(e) => setEdited(e.target.value)}
      />
      <button
        className="sand-button text-sm mt-2 w-full"
        onClick={() => onGenerate(edited)}
        disabled={isLoading || !edited.trim()}
      >
        {isLoading ? '⏳ 万相创作中…' : '🎨 让通义万相画出来'}
      </button>
    </div>
  );
}

function freeChatStarters(role) {
  return [
    `你好${VOICES[role.id]?.nickname || ''}，能介绍下你做的事吗？`,
    `${role.teamName}最重要的事是什么？`,
    `我可以问你一个具体的问题吗？`,
  ];
}

/* =====================================================================
   3) StudentView — 学生端 (任务+档案)
   ===================================================================== */

function StudentView({ student, patchStudent, knowledge, agentOverrides, weaponOverrides, onAgent, onLogout, toast }) {
  const role = ROLES_BY_ID[student.roleId];
  const [view, setView] = useState('home');                    // home | stage | portfolio
  const [stageKey, setStageKey] = useState(student.currentStage);
  const [celebrate, setCelebrate] = useState(null);            // { stage, allDone, nextStage }

  function goStage(key) {
    if (student.stages[key].status === 'locked') {
      toast('这一层还没有解锁哦，先完成上一阶段。', 'error');
      return;
    }
    setStageKey(key);
    setView('stage');
  }

  async function completeStage(key) {
    const order = ['learn','ask','think','debate','act'];
    const idx = order.indexOf(key);
    const next = order[idx + 1];
    patchStudent(s => {
      const ns = { ...s, stages: { ...s.stages } };
      ns.stages[key] = { ...ns.stages[key], status: 'completed', reviewStatus: 'pending', submittedAt: new Date().toISOString(), teacherScore: null, teacherNote: '', reviewedAt: null };
      ns.stages[key].aiScore = aiSuggestScore({
        student: s, role, stage: key,
        submission: ns.stages[key].submission, conversation: ns.stages[key].conversation,
      });
      if (next && ns.stages[next].status === 'locked') {
        ns.stages[next] = { ...ns.stages[next], status: 'in-progress' };
        ns.currentStage = next;
      } else if (!next) {
        ns.currentStage = 'act';
      }
      return ns;
    });
    try { await SandPlatform.flush(); } catch (e) { toast('提交尚未保存：' + e.message, 'error'); return; }
    const stage = STAGES.find(s => s.key === key);
    setCelebrate({ stage, allDone: !next, nextStage: next });
  }

  function dismissCelebrate() {
    const { allDone, nextStage } = celebrate;
    setCelebrate(null);
    if (allDone) {
      setView('portfolio');
    } else if (nextStage) {
      setStageKey(nextStage);
    }
  }

  return (
    <Fragment>
      <TopBar
        left={<ProjectLogo small />}
        center={<RoleBadge role={role} />}
        right={
          <Fragment>
            <button className={`sand-button sand-button-ghost text-xs px-2.5 py-1.5 ${view==='home'?'!bg-sand-100':''}`} onClick={() => setView('home')}><span className="sm:hidden">🏠</span><span className="hidden sm:inline">🏠 沙盘首页</span></button>
            <button className={`sand-button sand-button-ghost text-xs px-2.5 py-1.5 ${view==='portfolio'?'!bg-sand-100':''}`} onClick={() => setView('portfolio')}><span className="sm:hidden">📿</span><span className="hidden sm:inline">📿 成长沙瓶</span></button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => onAgent && onAgent(role.id)} title="与其他智能体自由对话"><span className="sm:hidden">💬</span><span className="hidden sm:inline">💬 自由对话</span></button>
            <UserMenu label={student.name} sub={`${student.class} · ${student.team || '小组'}`} onLogout={onLogout}/>
          </Fragment>
        }
      />

      <div className="content-max px-5 sm:px-8 pb-14 student-workspace">
        {view === 'home' && (
          <StudentHome student={student} role={role} goStage={goStage} goPortfolio={() => setView('portfolio')} onExploreRole={onAgent} />
        )}
        {view === 'stage' && (
          <StudentStage
            key={`${student.id}:${stageKey}`}
            student={student} role={role} stageKey={stageKey}
            knowledge={knowledge?.[role.id] || []}
            agentOverrides={agentOverrides}
            weaponOverrides={weaponOverrides}
            onChangeStage={setStageKey}
            patchStudent={patchStudent}
            onComplete={() => completeStage(stageKey)}
            onHome={() => setView('home')}
            toast={toast}
          />
        )}
        {view === 'portfolio' && (
          <StudentPortfolio student={student} role={role} patchStudent={patchStudent} onHome={() => setView('home')}/>
        )}
      </div>
      {celebrate && (
        <CelebrateOverlay stage={celebrate.stage} role={role} allDone={celebrate.allDone} onClose={dismissCelebrate} />
      )}
    </Fragment>
  );
}

/* --------- Student Home --------- */

function StudentHome({ student, role, goStage, goPortfolio, onExploreRole }) {
  const completedCount = STAGES.filter(s => student.stages[s.key].status === 'completed').length;
  const percent = completedCount / STAGES.length * 100;
  const current = STAGES.find(s => s.key === student.currentStage);
  const allDone = completedCount === STAGES.length;

  return (
    <div className="grid lg:grid-cols-12 gap-6 mt-2 fade-up">
      {/* 左列：欢迎区 + 沙盘地图 */}
      <div className="lg:col-span-8 space-y-5">
        <div className="paper-card p-6 sm:p-7 relative overflow-hidden student-welcome">
          <div className="flex items-start gap-5">
            <div className="w-20 h-20 rounded-3xl shadow-sand-deep flex items-center justify-center text-4xl shrink-0"
                 style={{ background: role.bg }}>
              <RoleCharacter role={role} size={90} animate={false}/>
            </div>
            <div className="flex-1">
              <div className="text-xs text-sand-700 tracking-widest">{role.className} · {role.teamName}</div>
              <h1 className="font-display text-3xl sm:text-4xl text-sand-900 leading-tight">你好，{student.name}</h1>
              <div className="text-sand-800 mt-1">你的身份是 <span className="font-display text-sand-900">「{role.roleName}」</span></div>
              <p className="mt-3 text-sand-800 leading-relaxed">{role.mission}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <ProgressRing percent={percent} size={48} stroke={5} color={role.color} />
                  <div className="leading-tight">
                    <div className="text-xs text-sand-700">五阶段进度</div>
                    <div className="font-display text-lg text-sand-900">{completedCount} / 5 已完成</div>
                  </div>
                </div>
                {allDone ? (
                  <button className="sand-button" onClick={goPortfolio}>🏁 看看你的成长沙瓶 →</button>
                ) : (
                  <button className="sand-button" onClick={() => goStage(student.currentStage)}>
                    继续 {current?.icon} {current?.name} →
                  </button>
                )}
                {!allDone && <button className="sand-button sand-button-ghost text-sm" onClick={goPortfolio}>查看成长沙瓶</button>}
                {allDone && <button className="sand-button sand-button-ghost text-sm" onClick={() => goStage('act')}>回看「行」</button>}
              </div>
            </div>
          </div>
        </div>

        {/* 沙盘任务地图 */}
        <div className="map-board">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="font-display text-xl text-sand-900">九重探索地图</div>
              <div className="text-xs text-sand-700">点自己的项目继续学习，也可以拜访其他方向的导师。</div>
            </div>
            <Chip variant="gold">你在 {role.teamName}</Chip>
          </div>

          <div className="journey-map relative w-full aspect-[16/9] rounded-2xl overflow-hidden mt-2">
            <img className="journey-map-art" src="assets/exhibition/learning-landscape.jpg" alt="沙丘、绿洲与河谷组成的探索主题场景" loading="lazy"/>
            {ROLES.map(r => {
              const pos = MAP_POSITIONS[r.id];
              const me = r.id === role.id;
              return (
                <button key={r.id}
                     className={`map-pin ${me ? 'current' : ''}`}
                     style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                     onClick={() => me ? goStage(student.currentStage) : onExploreRole?.(r.id)}
                     aria-label={me ? `继续我的项目：${r.teamName}` : `拜访${r.roleName}`}
                     title={r.fullName}>
                  <div className="flex flex-col items-center">
                    <AIAvatar role={r} size={42}/>
                    <div className={`mt-1 text-[10px] font-medium leading-tight text-center bg-white/75 rounded-lg px-1.5 py-0.5 ${me ? 'text-sand-900' : 'text-sand-800'}`}>
                      {r.teamName}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 当前阶段任务卡 */}
        <div className="paper-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{current?.icon}</span>
              <span className="font-display text-lg text-sand-900">当前任务 · {current?.name}</span>
            </div>
            <Chip variant="gold">{current?.title} · {current?.verb}</Chip>
          </div>
          <p className="mt-3 text-sand-800 leading-relaxed">{role.stageTasks[student.currentStage]}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="sand-button" onClick={() => goStage(student.currentStage)}>开始本阶段 →</button>
            <button className="sand-button sand-button-ghost text-sm" onClick={goPortfolio}>看看自己的沙瓶</button>
          </div>
        </div>
      </div>

      {/* 右列：五阶段纵向地层 + 资料 */}
      <div className="lg:col-span-4 space-y-5">
        <div className="paper-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-sand-900">五阶段地层</div>
            <Chip variant="gold">学 · 问 · 思 · 辨 · 行</Chip>
          </div>
          <div className="stage-strata">
            {STAGES.map((s, i) => (
              <StageStratum
                key={s.key}
                stage={s}
                status={student.stages[s.key].status}
                idx={i}
                onClick={() => goStage(s.key)}
              />
            ))}
          </div>
        </div>

        <div className="paper-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-sand-900">课程资料 · {role.teamName}</div>
            <Chip variant="green">优先来源</Chip>
          </div>
          <div className="space-y-2">
            {role.materials.map((m, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-xl hover:bg-sand-50/60">
                <div className="w-8 h-8 rounded-lg bg-sand-100 flex items-center justify-center text-xs text-sand-700 shrink-0">{m.type[0]}</div>
                <div className="leading-tight">
                  <div className="text-sm font-medium text-sand-900">{m.title}</div>
                  <div className="text-xs text-sand-700 mt-0.5">{m.summary}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------- Student Stage (核心: 任务 + AI 对话 + 上传) --------- */

function StudentStage({ student, role, stageKey, knowledge, agentOverrides, weaponOverrides, onChangeStage, patchStudent, onComplete, onHome, toast }) {
  const stage = STAGES.find(s => s.key === stageKey);
  const stageData = student.stages[stageKey];
  const isCompleted = stageData.status === 'completed';

  const [tab, setTab] = useState('chat');                                 // chat | submit
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const sendingRef = useRef(false);
  const chatRef = useRef();

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [stageData.conversation.length, thinking]);

  async function send(overrideText) {
    const text = (typeof overrideText === 'string' ? overrideText : draft).trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true;
    const generation = SandPlatform.generation;
    const userMsg = { role: 'student', text, at: nowStr() };
    patchStudent(s => ({
      ...s,
      stages: { ...s.stages, [stageKey]: {
        ...s.stages[stageKey],
        conversation: [...s.stages[stageKey].conversation, userMsg],
      }}
    }));
    setDraft('');
    setThinking(true);
    const newHistory = [...stageData.conversation, userMsg];
    try {
      // mock 模式时加点延迟模拟"在思考"; qwen 模式不加,真实网络已经有延迟
      if (AI_CONFIG.provider === 'mock') {
        await new Promise(r => setTimeout(r, 700 + Math.random() * 500));
      }
      const aiResp = await aiChatAsync({
        mode: 'stage',
        roleId: role.id, stage: stageKey,
        message: text, history: stageData.conversation,
        knowledge: knowledge || [],
        student, agentOverrides, weaponOverrides,
      });
      if (generation !== SandPlatform.generation) return;
      const aiMsg = { role: 'ai', text: aiResp.text, at: nowStr(), source: aiResp.source, failed: aiResp.retriable, retryText: aiResp.retriable ? text : undefined };
      patchStudent(s => ({
        ...s,
        stages: { ...s.stages, [stageKey]: {
          ...s.stages[stageKey],
          conversation: [...s.stages[stageKey].conversation, aiMsg],
        }}
      }));
    } catch (e) {
      console.error(e);
      toast('AI 出错: ' + e.message, 'error');
    } finally {
      sendingRef.current = false;
      setThinking(false);
    }
  }

  function updateSubmission(patch) {
    patchStudent(s => ({
      ...s,
      stages: { ...s.stages, [stageKey]: {
        ...s.stages[stageKey],
        submission: { ...s.stages[stageKey].submission, ...patch },
      }}
    }));
  }

  function addMedia(m) {
    patchStudent(s => ({ ...s, stages: { ...s.stages, [stageKey]: { ...s.stages[stageKey], submission: { ...s.stages[stageKey].submission, media: [...(s.stages[stageKey].submission.media || []), m] } } } }));
    toast('已加入证据', 'success');
  }

  function removeMedia(m) {
    updateSubmission({ media: stageData.submission.media.filter(x => x.id !== m.id) });
  }

  function tryComplete() {
    const subm = stageData.submission;
    const hasMsgs = stageData.conversation.filter(c => c.role === 'student').length >= 1;
    const hasText = subm.text.trim().length >= 10;
    const hasReflect = subm.reflection.trim().length >= 8;
    if (!hasMsgs) { toast('先和你的项目智能体对话至少 1 次', 'error'); setTab('chat'); return; }
    if (!hasText) { toast('请把这一阶段的"内容"写下来（至少 10 字）', 'error'); setTab('submit'); return; }
    if (!hasReflect) { toast('反思字段还很短，请认真写下你的体会（至少 8 字）', 'error'); setTab('submit'); return; }
    onComplete();
  }

  const order = STAGES.map(s => s.key);
  const idx = order.indexOf(stageKey);
  const prev = order[idx - 1];
  const next = order[idx + 1];

  return (
    <div className="mt-2">
      {/* 阶段头部 */}
      <div className="paper-card p-5 sm:p-6 fade-up">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button className="sand-button-ghost sand-button text-xs px-3 py-1.5" onClick={onHome}>← 返回沙盘首页</button>
            <div>
              <div className="text-xs text-sand-700">{role.teamName} · 第 {idx+1} 关</div>
              <div className="font-display text-2xl sm:text-3xl text-sand-900 leading-tight">
                <span className="mr-2">{stage.icon}</span>{stage.name} <span className="text-sand-700 text-base font-normal">· {stage.title}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={!prev} className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => prev && onChangeStage(prev)}>← {prev ? STAGES.find(s=>s.key===prev).title : ''}</button>
            <button disabled={!next || student.stages[next]?.status === 'locked'}
                    className="sand-button sand-button-ghost text-xs px-3 py-1.5"
                    onClick={() => next && onChangeStage(next)}>{next ? STAGES.find(s=>s.key===next).title : ''} →</button>
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <div className="text-xs text-sand-700 mb-1 tracking-widest">本关任务</div>
            <p className="text-sand-900 leading-relaxed">{role.stageTasks[stageKey]}</p>
          </div>
          <div>
            <div className="text-xs text-sand-700 mb-1 tracking-widest">阶段心法</div>
            <p className="text-sand-800 italic leading-relaxed">{stage.intro}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {isCompleted && <span className="stamp">已 完 成</span>}
          {stageData.aiScore && <Chip variant="blue">规则参考 {stageData.aiScore.overall} / 5</Chip>}
          {stageData.teacherScore && <Chip variant="green">教师确认</Chip>}
        </div>
      </div>

      {/* 主交互区: 左 = 对话 / 右 = 提交 */}
      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        <div className="lg:col-span-7 paper-card p-0 overflow-hidden flex flex-col" style={{ minHeight: 560 }}>
          {/* 标签条 */}
          <div className="stage-chat-toolbar px-5 pt-4 pb-2 flex items-center justify-between border-b border-sand-300/30">
            <div className="tabs">
              <button className={tab==='chat' ? 'active' : ''} onClick={() => setTab('chat')}>💬 与 AI 对话</button>
              <button className={tab==='submit' ? 'active' : ''} onClick={() => setTab('submit')}>📥 提交本阶段</button>
            </div>
            <div className="flex items-center gap-2">
              <AIAvatar role={role} size={36} thinking={thinking} />
              <div className="text-xs leading-tight">
                <div className="text-sand-900 font-medium">{role.roleName}</div>
                <div className="text-sand-700">{thinking ? '正在思考…' : '引导式辅导，不代写答案'}</div>
              </div>
            </div>
          </div>

          {tab === 'chat' && (
            <Fragment>
              <div ref={chatRef} className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-2.5">
                <div className="chat-bubble system">
                  欢迎来到「{stage.name}」。把你的想法告诉我，我会用提问陪你把它变得更清楚。
                </div>
                {stageData.conversation.length === 0 && (
                  <div className="chat-bubble ai">
                    {stageOpener(role, stageKey)}
                  </div>
                )}
                {stageData.conversation.map((m, i) => (
                  <div key={i} className={`chat-bubble ${m.role}`}>
                    {m.source && <div className="text-[10px] mb-1 opacity-70">{m.source === 'proxy' || m.source === 'qwen' ? '真实 AI 回复 · 请核对事实' : m.source === 'error' ? '真实 AI 未成功' : '本地教学模拟 · 非真实 AI 回复'}</div>}
                    {m.role === 'ai' ? renderMarkdown(m.text) : m.text}
                    {m.failed && m.retryText && <button className="sand-button sand-button-ghost mt-2" disabled={thinking} onClick={() => send(m.retryText)}>重新请求真实 AI</button>}
                  </div>
                ))}
                {thinking && (
                  <div className="chat-bubble ai inline-flex items-center gap-2">
                    <span>{role.roleName} 正在思考</span>
                    <span className="typing-dots"><span /><span /><span /></span>
                  </div>
                )}
              </div>

              <div className="border-t border-sand-300/30 px-4 py-3">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {quickPrompts(role, stageKey).map((p, i) => (
                    <button key={i} className="chip" onClick={() => setDraft(p)}>{p}</button>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2}
                    className="flex-1 px-3 py-2.5 rounded-xl bg-white/80 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
                    placeholder={`告诉 ${role.roleName}：你的想法 / 卡点 / 计划…`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
                  />
                  <button className="sand-button" onClick={send} disabled={!draft.trim() || thinking}>
                    发送
                  </button>
                </div>
                <div className="text-[11px] text-sand-700 mt-1">⌘/Ctrl + Enter 发送 · AI 会提问引导，不会替你写答案</div>
              </div>
            </Fragment>
          )}

          {tab === 'submit' && (
            <div className="flex-1 overflow-auto p-5 space-y-5">
              <div>
                <div className="text-sm font-medium text-sand-900 mb-1">📝 本阶段内容（任务交付）</div>
                <div className="text-xs text-sand-700 mb-2">用你自己的话，写下本阶段完成的核心内容（方案 / 答案 / 笔记 / 路演稿）。</div>
                <textarea
                  rows={6}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/85 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
                  placeholder="例如：我的方案是…… 我的关键问题是…… 我的最终路演稿是……"
                  value={stageData.submission.text}
                  disabled={isCompleted}
                  onChange={(e) => updateSubmission({ text: e.target.value })}
                />
                <div className="text-[11px] text-sand-700 mt-1">字数：{stageData.submission.text.length}</div>
              </div>

              <div>
                <div className="text-sm font-medium text-sand-900 mb-1">📿 我的反思</div>
                <div className="text-xs text-sand-700 mb-2">这一阶段我学到了什么？我做对了/做错了什么？下一步我准备……</div>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/85 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
                  placeholder="把心里的话写下来。"
                  value={stageData.submission.reflection}
                  disabled={isCompleted}
                  onChange={(e) => updateSubmission({ reflection: e.target.value })}
                />
              </div>

              <div>
                <div className="text-sm font-medium text-sand-900 mb-1">🪣 上传证据（图片 / 视频 / 音频 / 文档）</div>
                {!isCompleted && <UploadZone onUpload={addMedia} />}
                {stageData.submission.media.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                    {stageData.submission.media.map(m => (
                      <MediaThumb key={m.id} media={m} onDelete={!isCompleted ? removeMedia : null} />
                    ))}
                  </div>
                )}
              </div>

              {!isCompleted && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                  <div className="text-xs text-sand-700">
                    完成后生成规则参考评分，提交老师审核，并解锁下一关。
                  </div>
                  <button className="sand-button" onClick={tryComplete}>提交并完成本关 →</button>
                </div>
              )}

              {isCompleted && stageData.aiScore && (
                <div className="paper-card p-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-lg text-sand-900">本关规则参考评分</div>
                    <Chip variant="blue">{stageData.aiScore.overall} / 5</Chip>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-sm">
                    {RUBRIC_DIMS.map(d => (
                      <div key={d.key} className="flex items-center justify-between bg-white/60 rounded-lg p-2">
                        <div>
                          <div className="text-sand-900 font-medium">{d.name}</div>
                          <div className="text-[11px] text-sand-700">{stageData.aiScore.reasons[d.key]}</div>
                        </div>
                        <div className="font-display text-xl text-sand-800 tabular-nums">{stageData.aiScore[d.key]}</div>
                      </div>
                    ))}
                  </div>
                  {stageData.teacherScore ? (
                    <div className="mt-3 chip green">教师确认评分 · 平均 {avgScore(stageData.teacherScore)}</div>
                  ) : (
                    <div className="mt-3 text-xs text-sand-700">已提交，等待教师审核。这些参考分按文字、对话及证据数量计算，不代表 AI 已理解附件内容。</div>
                  )}
                  {stageData.teacherNote && <p className="text-sm mt-3 text-sand-800">老师反馈：{stageData.teacherNote}</p>}
                  <button className="sand-button sand-button-ghost mt-3" onClick={() => { if (confirm('重新修改后，原评分将失效，需要再次提交审核。继续吗？')) patchStudent(s => ({ ...s, stages: { ...s.stages, [stageKey]: { ...s.stages[stageKey], status: 'in-progress', aiScore: null } } })); }}>重新修改本关</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右栏：本阶段提示 + 课程资料 + 历史片段 */}
        <div className="lg:col-span-5 space-y-4">
          <div className="paper-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg text-sand-900">本阶段心法</div>
              <Chip variant="gold">{stage.title}</Chip>
            </div>
            <p className="text-sand-800 leading-relaxed">{stage.intro}</p>
            <div className="divider-sand" />
            <div className="text-sm font-medium text-sand-900 mb-1">关键词雷达</div>
            <div className="flex flex-wrap gap-1.5">
              {role.keywords.map(kw => {
                const hit = stageData.conversation.some(m => m.role==='student' && m.text.includes(kw))
                  || stageData.submission.text.includes(kw)
                  || stageData.submission.reflection.includes(kw);
                return (
                  <span key={kw} className={`chip ${hit ? 'green' : ''}`}>{hit ? '✓ ' : ''}{kw}</span>
                );
              })}
            </div>
          </div>

          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-2">课程资料 · {role.teamName}</div>
            <div className="space-y-2">
              {role.materials.map((m, i) => (
                <div key={i} className="rounded-xl bg-white/60 p-3 border border-sand-300/30">
                  <div className="text-[11px] text-sand-700 tracking-widest">{m.type}</div>
                  <div className="text-sm font-medium text-sand-900">{m.title}</div>
                  <div className="text-xs text-sand-700 mt-1">{m.summary}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-2">五阶段地层</div>
            <div className="stage-strata">
              {STAGES.map((s, i) => (
                <StageStratum
                  key={s.key} stage={s} idx={i}
                  status={student.stages[s.key].status}
                  onClick={() => student.stages[s.key].status !== 'locked' && onChangeStage(s.key)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function quickPrompts(role, stage) {
  const base = {
    learn:  ['我想从哪里开始？', '资料里我没看懂的部分是…', '我注意到一个细节：'],
    ask:    ['我最想搞清楚的是…', '我担心…', '我能不能换个角度问：'],
    think:  ['我画了一张草图…', '我打算先做…', '我卡在…'],
    debate: ['我比较了两版后…', '同伴的意见是…', '我决定…，因为'],
    act:    ['我完成了…', '我拍了…张照片', '讲解时我打算说：'],
  };
  return base[stage] || [];
}

function stageOpener(role, stage) {
  const stageObj = STAGES.find(s => s.key === stage);
  const openers = {
    learn:  `${stageObj.icon} 我是${role.roleName}。先简单聊一下——你打开「${role.teamName}」的资料后，最让你"咦？"的一句话或一个画面是什么？说一个就行。`,
    ask:    `${stageObj.icon} ${role.teamName}的关键问题来了：「${role.stageTasks.ask.split('；')[0]}」。你心里冒出来的第一个版本是什么？哪怕只是半句话，先说出来。`,
    think:  `${stageObj.icon} 走到「思的绿洲」了。先告诉我：你打算怎么开始？画一张草图、列一份清单、写一句话——哪个对你最容易？`,
    debate: `${stageObj.icon} 「辨」最有意思的部分是——你心里其实已经选了一版，对吗？告诉我你倾向哪一版，再说说"另一版我不选"的1条理由。`,
    act:    `${stageObj.icon} 终点站。先告诉我：你打算让看你作品的人，记住的"一件事"是什么？把这件事说清楚，剩下的就都好办了。`,
  };
  return openers[stage] || '🌱 我们从一个简单的问题开始：你看完本阶段任务后，最先想到的"困惑"是什么？';
}

/* --------- Student Portfolio (成长沙瓶) --------- */

function StudentPortfolio({ student, role, patchStudent, onHome }) {
  const overall = studentOverallScore(student);
  return (
    <div className="mt-2 grid lg:grid-cols-12 gap-5 fade-up">
      <div className="lg:col-span-4 space-y-5">
        <div className="paper-card p-5 text-center">
          <div className="text-xs text-sand-700 mb-2 tracking-widest">{student.class} · {role.teamName}</div>
          <div className="font-display text-2xl text-sand-900">{student.name} 的成长沙瓶</div>
          <div className="text-xs text-sand-700 mt-1">每完成一关，沙瓶就多一层。</div>
          <div className="mt-4">
            <SandJar student={student} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white/55 rounded-xl p-3">
              <div className="text-xs text-sand-700">已完成关</div>
              <div className="font-display text-2xl text-sand-900">{STAGES.filter(s=>student.stages[s.key].status==='completed').length} <span className="text-base text-sand-700">/ 5</span></div>
            </div>
            <div className="bg-white/55 rounded-xl p-3">
              <div className="text-xs text-sand-700">综合表现</div>
              <div className="font-display text-2xl text-sand-900">{overall ? overall : '—'} <span className="text-base text-sand-700">/ 5</span></div>
            </div>
          </div>
          <button className="sand-button sand-button-ghost text-xs mt-4" onClick={onHome}>← 返回沙盘首页</button>
        </div>

        <div className="paper-card p-5">
          <div className="font-display text-lg text-sand-900 mb-2">我的项目记录</div>
          <textarea
            rows={5}
            className="w-full px-3 py-2.5 rounded-xl bg-white/85 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
            placeholder="写下你对整个一沙一世界项目最深的一句话。"
            value={student.portfolioReflection}
            onChange={(e) => patchStudent(s => ({ ...s, portfolioReflection: e.target.value }))}
          />
        </div>
      </div>

      <div className="lg:col-span-8 space-y-4">
        {STAGES.map((s, i) => {
          const data = student.stages[s.key];
          return (
            <div key={s.key} className="paper-card p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{s.icon}</span>
                  <span className="font-display text-lg text-sand-900">{s.title} · {s.name}</span>
                </div>
                {data.status === 'completed' && data.aiScore && <Chip variant="blue">规则参考 {data.aiScore.overall} / 5</Chip>}
                {data.status === 'in-progress' && <Chip variant="gold">进行中</Chip>}
                {data.status === 'locked' && <Chip>待解锁</Chip>}
              </div>

              {data.status !== 'locked' ? (
                <Fragment>
                  {data.submission.text && (
                    <div className="text-sm text-sand-900 whitespace-pre-wrap leading-relaxed mb-2">
                      {data.submission.text}
                    </div>
                  )}
                  {data.submission.reflection && (
                    <div className="text-xs text-sand-700 italic border-l-2 border-sand-400/50 pl-2 mb-3">
                      📿 {data.submission.reflection}
                    </div>
                  )}
                  {data.submission.media.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                      {data.submission.media.map(m => <MediaThumb key={m.id} media={m} />)}
                    </div>
                  )}
                  {data.teacherScore && (
                    <div className="bg-oasis-500/10 rounded-lg p-2 text-xs text-sand-900">
                      🎓 教师评分：{avgScore(data.teacherScore)} / 5　{data.teacherNote && `— ${data.teacherNote}`}
                    </div>
                  )}
                </Fragment>
              ) : (
                <div className="text-sm text-sand-700">完成上一阶段后解锁。</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================================
   3) TeacherView
   ===================================================================== */

function TeacherView({ state, setState, subView, setSubView, onLogout, toast }) {
  const navigationGuardRef = useRef(null);
  const registerNavigationGuard = useCallback(guard => { navigationGuardRef.current = guard; }, []);
  function navigate(next) {
    if (next.kind === subView.kind) return;
    if (navigationGuardRef.current && !navigationGuardRef.current()) return;
    setSubView(next);
  }
  function logout() {
    if (navigationGuardRef.current && !navigationGuardRef.current()) return;
    onLogout();
  }
  return (
    <Fragment>
      <TopBar
        left={<ProjectLogo small />}
        center={<div className="text-sm text-sand-700 tracking-widest">教师工作台 · TEACHER CONSOLE</div>}
        right={
          <Fragment>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'overview'})}><span className="hidden sm:inline">📊 </span>总览</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'review'})}>审核评分</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'agent-tune'})}><span className="hidden sm:inline">🎭 </span>角色调参</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'weapon-tune'})}><span className="hidden sm:inline">🪖 </span>武器调参</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'knowledge'})}><span className="hidden sm:inline">📚 </span>知识库</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'showcase'})}><span className="hidden sm:inline">🌾 </span>成果展</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'ai-settings'})}><span className="hidden sm:inline">⚙️ </span>AI 设置</button>
            <button className="sand-button sand-button-ghost text-xs px-2.5 py-1.5" onClick={() => navigate({kind:'data'})}>数据与布展</button>
            <UserMenu label="林老师" sub="项目总协调" onLogout={logout} />
          </Fragment>
        }
      />
      <div className="content-max px-5 sm:px-8 pb-14 teacher-workspace">
        {subView.kind === 'overview'     && <TeacherOverview state={state} setSubView={navigate}/>}
        {subView.kind === 'agent-tune'   && <TeacherAgentTune state={state} setState={setState} initialRoleId={subView.roleId} registerNavigationGuard={registerNavigationGuard} onBack={() => navigate({ kind: 'overview' })} toast={toast}/>}
        {subView.kind === 'weapon-tune'  && <TeacherWeaponTune state={state} setState={setState} initialWeaponId={subView.weaponId} registerNavigationGuard={registerNavigationGuard} onBack={() => navigate({ kind: 'overview' })} toast={toast}/>}
        {subView.kind === 'knowledge'    && <TeacherKnowledgeManager state={state} setState={setState} onBack={() => setSubView({ kind: 'overview' })} toast={toast} />}
        {subView.kind === 'review' && <TeacherReviewManager state={state} setState={setState} registerNavigationGuard={registerNavigationGuard} onBack={() => navigate({kind:'overview'})} onOpenShowcase={initialSubmission => navigate({kind:'showcase',initialSubmission})} toast={toast}/>}
        {subView.kind === 'showcase' && <TeacherShowcaseEditor state={state} setState={setState} initialSubmission={subView.initialSubmission} registerNavigationGuard={registerNavigationGuard} onBack={() => navigate({kind:'overview'})} toast={toast}/>}
        {subView.kind === 'data' && <PlatformDataManager state={state} onBack={() => navigate({kind:'overview'})} toast={toast}/>}
        {subView.kind === 'ai-settings'  && <PlatformAISettings state={state} onBack={() => setSubView({ kind: 'overview' })} toast={toast} />}
      </div>
    </Fragment>
  );
}

/* ---------- 教师: 知识库管理 ---------- */
function TeacherKnowledgeManager({ state, setState, onBack, toast }) {
  const [activeRole, setActiveRole] = useState(ROLES[0].id);
  const role = ROLES_BY_ID[activeRole];
  const items = state.knowledge?.[activeRole] || [];

  const [draftTitle, setDraftTitle] = useState('');
  const [draftTag, setDraftTag] = useState('资料');
  const [draftContent, setDraftContent] = useState('');
  const [draftMedia, setDraftMedia] = useState(null);
  const [kbDetail, setKbDetail] = useState(null);   // 点开的资料详情
  const [editMode, setEditMode] = useState(false);  // 编辑模式
  const [editForm, setEditForm] = useState(null);

  function addItem() {
    if (!draftTitle.trim() || !draftContent.trim()) { toast('请填写标题和内容', 'error'); return; }
    const item = {
      id: 'kb-' + Date.now(),
      kind: draftMedia ? draftMedia.kind : 'text',
      tag: draftTag,
      title: draftTitle.trim(),
      content: draftContent.trim(),
      media: draftMedia,
      addedAt: new Date().toISOString().slice(0,10),
      addedBy: '林老师',
    };
    setState(s => ({
      ...s,
      knowledge: { ...s.knowledge, [activeRole]: [item, ...(s.knowledge?.[activeRole] || [])] },
    }));
    setDraftTitle(''); setDraftContent(''); setDraftMedia(null); setDraftTag('资料');
    toast(`已为「${role.fullName}」补充知识`, 'success');
  }

  function removeItem(id) {
    setState(s => ({
      ...s,
      knowledge: { ...s.knowledge, [activeRole]: (s.knowledge?.[activeRole] || []).filter(it => it.id !== id) },
    }));
    toast('已移除', 'success');
  }

  return (
    <div className="mt-2 fade-up">
      <div className="paper-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onBack}>← 返回</button>
            <div>
              <div className="font-display text-xl text-sand-900">📚 智能体知识库管理</div>
              <div className="text-xs text-sand-700">为每个角色智能体补充课程资料：文字介绍、参考文档、图片、操作要点。</div>
            </div>
          </div>
          <Chip variant="gold">共 {Object.values(state.knowledge || {}).reduce((a, v) => a + v.length, 0)} 条已装载</Chip>
        </div>

        <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {ROLES.map(r => {
            const n = (state.knowledge?.[r.id] || []).length;
            return (
              <button key={r.id}
                      className={`rounded-2xl p-2 border ${r.id === activeRole ? 'border-sand-500 bg-sand-100/80' : 'border-sand-300/30 hover:bg-sand-50/70'} transition`}
                      onClick={() => setActiveRole(r.id)}>
                <div className="flex justify-center">
                  <RoleCharacter role={r} size={70} animate={false} />
                </div>
                <div className="text-[10px] text-sand-800 mt-1 truncate text-center">{r.teamName}</div>
                <div className="text-[10px] text-sand-700 text-center">📚 {n}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        {/* 添加资料 */}
        <div className="lg:col-span-5">
          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-1">为 {role.fullName} 补充资料</div>
            <div className="text-xs text-sand-700 mb-3">填写的文字会供自由对话和阶段任务参考。图片、PDF、Office 附件请把关键内容另行填入，不会自动识别。</div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-sand-700">类别</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {['资料包','案例','工具','参考','规范','补充'].map(t => (
                    <button key={t}
                            className={`chip ${draftTag === t ? 'gold' : ''}`}
                            onClick={() => setDraftTag(t)}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-sand-700">标题</label>
                <input
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400"
                  placeholder="例如：夯土的正确配比"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-sand-700">内容</label>
                <textarea
                  rows={6}
                  className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 resize-none"
                  placeholder="把要让智能体掌握的要点写在这里。"
                  value={draftContent}
                  onChange={(e) => setDraftContent(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-sand-700">附件（图片/文档，可选）</label>
                <div className="mt-1">
                  {draftMedia ? (
                    <div className="flex items-center gap-2 p-2 bg-white/55 rounded-xl border border-sand-300/30">
                      <span className="text-2xl">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[draftMedia.kind] || '📎'}</span>
                      <div className="flex-1 text-xs text-sand-800 truncate">{draftMedia.label}</div>
                      <button className="text-xs text-terra-500" onClick={() => setDraftMedia(null)}>移除</button>
                    </div>
                  ) : (
                    <UploadZone key={activeRole} multiple={false} onUpload={(m) => setDraftMedia(m)} hint="把文档或图片拖到这里" />
                  )}
                </div>
              </div>
              <button className="sand-button w-full" onClick={addItem}>装载到 {role.teamName} 的知识库 →</button>
            </div>
          </div>
        </div>

        {/* 现有资料 */}
        <div className="lg:col-span-7">
          <div className="paper-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg text-sand-900">已装载资料 · {role.teamName} · {items.length} 篇</div>
              <Chip variant="green">优先级：从上到下</Chip>
            </div>
            {items.length === 0 ? (
              <div className="text-sm text-sand-700">这个智能体还没有资料。在左侧表单添加吧。</div>
            ) : (
              <div className="space-y-2 max-h-[560px] overflow-auto pr-1">
                {items.map(it => (
                  <button key={it.id}
                          onClick={() => { setKbDetail(it); setEditMode(false); }}
                          className="w-full text-left bg-white/55 hover:bg-white/85 rounded-xl p-3 border border-sand-300/30 hover:border-sand-400/60 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] tracking-widest text-sand-700 bg-sand-200/60 rounded px-1.5 py-0.5">{it.tag || '资料'}</span>
                          <span className="font-medium text-sand-900 text-sm">{it.title}</span>
                          <span className="ml-auto text-[10px] text-sand-700">点开 →</span>
                        </div>
                        <div className="text-xs text-sand-700 mt-1.5 leading-relaxed line-clamp-2">{it.content}</div>
                        {it.media && (
                          <div className="mt-2 inline-flex items-center gap-2 bg-white/70 rounded-lg px-2 py-1 text-xs text-sand-800 border border-sand-300/30">
                            <span>{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[it.media.kind] || '📎'}</span>
                            <span>{it.media.label}</span>
                          </div>
                        )}
                        <div className="text-[10px] text-sand-700 mt-1.5">{it.addedBy} · {it.addedAt}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 单篇资料详情 modal — 老师视角, 可查看也可编辑/删除 */}
      <Modal open={!!kbDetail} onClose={() => { setKbDetail(null); setEditMode(false); }} maxW="max-w-3xl">
        {kbDetail && (
          <div>
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-[10px] tracking-widest text-sand-700 bg-sand-200/60 rounded px-1.5 py-0.5">{kbDetail.tag || '资料'}</span>
                  <span className="text-[10px] text-sand-700">{kbDetail.addedBy} · {kbDetail.addedAt}</span>
                  {kbDetail.id?.startsWith('kb-seed-') && <Chip>系统种子</Chip>}
                </div>
                {!editMode ? (
                  <div className="font-display text-2xl text-sand-900 leading-tight">{kbDetail.title}</div>
                ) : (
                  <input
                    className="w-full font-display text-2xl text-sand-900 leading-tight bg-white/85 border border-sand-300/60 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-sand-400"
                    value={editForm?.title || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                  />
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!editMode ? (
                  <Fragment>
                    <button className="sand-button sand-button-ghost text-xs px-3 py-1.5"
                            onClick={() => { setEditMode(true); setEditForm({ title: kbDetail.title, tag: kbDetail.tag, content: kbDetail.content }); }}>
                      ✏️ 编辑
                    </button>
                    <button className="sand-button sand-button-ghost text-xs px-3 py-1.5 text-terra-500"
                            onClick={() => {
                              if (!confirm(`确定删除「${kbDetail.title}」吗?`)) return;
                              removeItem(kbDetail.id);
                              setKbDetail(null);
                            }}>🗑 删除</button>
                  </Fragment>
                ) : (
                  <Fragment>
                    <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => setEditMode(false)}>取消</button>
                    <button className="sand-button text-xs px-3 py-1.5"
                            onClick={() => {
                              setState(s => ({
                                ...s,
                                knowledge: {
                                  ...s.knowledge,
                                  [activeRole]: (s.knowledge?.[activeRole] || []).map(it =>
                                    it.id === kbDetail.id ? { ...it, title: editForm.title, content: editForm.content, tag: editForm.tag } : it
                                  ),
                                },
                              }));
                              setKbDetail({ ...kbDetail, title: editForm.title, content: editForm.content, tag: editForm.tag });
                              setEditMode(false);
                              toast('已保存修改', 'success');
                            }}>💾 保存</button>
                  </Fragment>
                )}
                <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => { setKbDetail(null); setEditMode(false); }}>关闭</button>
              </div>
            </div>
            <div className="divider-sand" />

            {editMode && (
              <div className="mb-3">
                <div className="text-xs text-sand-700 mb-1">类别</div>
                <div className="flex flex-wrap gap-1.5">
                  {['资料包','案例','工具','参考','规范','补充'].map(t => (
                    <button key={t}
                            className={`chip ${editForm?.tag === t ? 'gold' : ''}`}
                            onClick={() => setEditForm(f => ({ ...f, tag: t }))}>{t}</button>
                  ))}
                </div>
              </div>
            )}

            {!editMode ? (
              <div className="text-sand-900 leading-relaxed whitespace-pre-wrap text-[15px]">{kbDetail.content}</div>
            ) : (
              <textarea
                rows={12}
                className="w-full px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 resize-y text-[15px] leading-relaxed"
                value={editForm?.content || ''}
                onChange={(e) => setEditForm(f => ({ ...f, content: e.target.value }))}
              />
            )}

            {kbDetail.media && !editMode && (
              <div className="mt-4">
                <div className="text-xs text-sand-700 tracking-widest mb-2">📎 附件</div>
                {kbDetail.media.kind === 'image' && kbDetail.media.data && (
                  <img src={kbDetail.media.data} alt={kbDetail.media.label} className="max-w-full rounded-xl border border-sand-300/40" />
                )}
                {kbDetail.media.kind === 'video' && kbDetail.media.data && (
                  <video src={kbDetail.media.data} controls className="max-w-full rounded-xl border border-sand-300/40" />
                )}
                {kbDetail.media.kind === 'audio' && kbDetail.media.data && (
                  <audio src={kbDetail.media.data} controls className="w-full" />
                )}
                {(!kbDetail.media.data || kbDetail.media.kind === 'doc') && (
                  <div className="bg-white/55 rounded-xl p-3 text-sm flex items-center gap-2">
                    <span className="text-2xl">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[kbDetail.media.kind] || '📎'}</span>
                    <span>{kbDetail.media.label}</span>
                  </div>
                )}
              </div>
            )}
            <div className="divider-sand" />
            <div className="text-xs text-sand-700 italic">
              💡 这条资料会被 <b>{role.roleName}</b> 在自由对话时作为参考。智能体主要靠自己的 AI 推理能力,不会照搬这段话。
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ---------- 教师: AI 设置 (切换 proxy / mock / qwen) ---------- */
function TeacherAISettings({ onBack, toast }) {
  const [provider, setProvider] = useState(AI_CONFIG.provider);
  const [apiKey, setApiKey] = useState(AI_CONFIG.qwen.apiKey);
  const [model, setModel] = useState(AI_CONFIG.qwen.model);
  const [endpoint, setEndpoint] = useState(AI_CONFIG.qwen.endpoint);
  const [temperature, setTemperature] = useState(AI_CONFIG.qwen.temperature);
  const [fallback, setFallback] = useState(AI_CONFIG.fallbackToMockOnError);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    checkProxyHealth().then(setHealth);
  }, []);

  function save() {
    AI_CONFIG.provider = provider;
    AI_CONFIG.qwen.apiKey = apiKey.trim();
    AI_CONFIG.qwen.model = model.trim() || 'qwen-plus';
    AI_CONFIG.qwen.endpoint = endpoint.trim();
    AI_CONFIG.qwen.temperature = parseFloat(temperature) || 0.7;
    AI_CONFIG.fallbackToMockOnError = fallback;
    saveAIConfig();
    toast('AI 设置已保存', 'success');
  }

  async function testQwen() {
    setTesting(true); setTestResult(null);
    AI_CONFIG.qwen.apiKey = apiKey.trim();
    AI_CONFIG.qwen.model = model.trim() || 'qwen-plus';
    AI_CONFIG.qwen.endpoint = endpoint.trim();
    AI_CONFIG.qwen.temperature = parseFloat(temperature) || 0.7;
    try {
      const text = await callQwen({
        systemPrompt: '你是一个测试助手。用一句话(20字以内)回应这句话。',
        messages: [{ role: 'user', content: '你好,请用一句话证明你是千问。' }],
      });
      setTestResult({ ok: true, text, channel: 'direct' });
    } catch (e) {
      setTestResult({ ok: false, text: e.message, channel: 'direct' });
    } finally {
      setTesting(false);
    }
  }

  async function testProxy() {
    setTesting(true); setTestResult(null);
    try {
      const text = await callProxy({
        systemPrompt: '你是一个测试助手。用一句话(20字以内)回应。',
        messages: [{ role: 'user', content: '你好,请用一句话证明你是千问。' }],
      });
      setTestResult({ ok: true, text, channel: 'proxy' });
    } catch (e) {
      setTestResult({ ok: false, text: e.message, channel: 'proxy' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="mt-2 fade-up">
      <div className="paper-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onBack}>← 返回</button>
            <div>
              <div className="font-display text-xl text-sand-900">⚙️ AI 引擎设置</div>
              <div className="text-xs text-sand-700">切换模拟模式和真实的千问 (Qwen) API。所有 9 个智能体共享此设置。</div>
            </div>
          </div>
          <Chip variant={AI_CONFIG.provider === 'mock' ? 'gold' : 'green'}>
            当前: {AI_CONFIG.provider === 'proxy' ? '🛡 后端代理 (千问)'
                   : AI_CONFIG.provider === 'mock' ? '🎭 模拟模式'
                   : '🪶 浏览器直连千问'}
          </Chip>
        </div>

        {/* 后端健康状态 */}
        {health && (
          <div className={`mt-4 rounded-xl p-3 text-sm flex items-center justify-between flex-wrap gap-2 ${
            health.ok && health.hasKey ? 'bg-oasis-500/15' : 'bg-terra-500/15'
          }`}>
            <div className="text-sand-900">
              <b>后端代理状态:</b> {health.ok ? '✅ 在线' : '❌ 不可达'}
              {health.ok && (
                <span> · API Key: {health.hasKey ? `✅ 已配置 (尾号 ${health.keyTail})` : '❌ 未配置 (.env)'}</span>
              )}
              {health.ok && health.model && <span> · 模型: <code className="bg-sand-100 px-1 rounded">{health.model}</code></span>}
            </div>
            <button className="text-xs text-sand-700 hover:underline" onClick={async () => setHealth(await checkProxyHealth())}>刷新</button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        {/* 左:provider选择 + 配置 */}
        <div className="lg:col-span-7 space-y-4">
          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-3">选择 AI 提供方</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <button onClick={() => setProvider('proxy')}
                      className={`rounded-2xl p-4 border-2 transition text-left ${provider === 'proxy' ? 'border-oasis-500 bg-oasis-500/10' : 'border-sand-300/30 hover:bg-sand-50/60'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-2xl">🛡</span>
                  <span className="font-display text-lg text-sand-900">后端代理</span>
                  {provider === 'proxy' && <Chip variant="green">已选</Chip>}
                  <Chip variant="gold">推荐</Chip>
                </div>
                <div className="text-xs text-sand-700 mt-2 leading-relaxed">
                  <b>生产部署用</b>。API Key 存于后端 .env,不暴露给浏览器。<br/>
                  前端调用 <code className="bg-sand-100 px-1 rounded text-[10px]">/api/chat</code>,后端代转千问。
                </div>
              </button>
              <button onClick={() => setProvider('mock')}
                      className={`rounded-2xl p-4 border-2 transition text-left ${provider === 'mock' ? 'border-sand-500 bg-sand-100/70' : 'border-sand-300/30 hover:bg-sand-50/60'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎭</span>
                  <span className="font-display text-lg text-sand-900">模拟模式</span>
                  {provider === 'mock' && <Chip variant="gold">已选</Chip>}
                </div>
                <div className="text-xs text-sand-700 mt-2 leading-relaxed">
                  前端模板。<b>零网络</b>,离线、免费。<br/>
                  适合演示、开发期、断网课堂。
                </div>
              </button>
              <button onClick={() => setProvider('qwen')}
                      className={`rounded-2xl p-4 border-2 transition text-left ${provider === 'qwen' ? 'border-terra-500 bg-terra-500/10' : 'border-sand-300/30 hover:bg-sand-50/60'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-2xl">🪶</span>
                  <span className="font-display text-lg text-sand-900">直连千问</span>
                  {provider === 'qwen' && <Chip variant="terra">已选</Chip>}
                </div>
                <div className="text-xs text-sand-700 mt-2 leading-relaxed">
                  浏览器直接调千问 API。<br/>
                  <b className="text-terra-500">⚠️ 仅开发期</b>,API Key 会暴露给前端。
                </div>
              </button>
            </div>
          </div>

          {provider === 'qwen' && (
            <div className="paper-card p-5">
              <div className="font-display text-lg text-sand-900 mb-3">🪶 千问 API 配置</div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-sand-700">API Key (sk- 开头)</label>
                  <input
                    type="password"
                    className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 font-mono text-sm"
                    placeholder="sk-xxxxxxxxxxxxxxxx"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)} />
                  <div className="text-[11px] text-sand-700 mt-1">
                    在阿里云百炼控制台获取。仅保存在本机 localStorage,不会上传任何服务器。
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-sand-700">模型</label>
                    <select
                      className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}>
                      <option value="qwen3.6-plus">qwen3.6-plus (2026 最新,带思考模式 ⭐推荐)</option>
                      <option value="qwen3.5-plus">qwen3.5-plus (带思考模式)</option>
                      <option value="qwen3-max">qwen3-max (3 代最强)</option>
                      <option value="qwen-plus">qwen-plus (经典平衡)</option>
                      <option value="qwen-max">qwen-max (经典最强,慢)</option>
                      <option value="qwen-turbo">qwen-turbo (快,省)</option>
                      <option value="qwen-plus-latest">qwen-plus-latest</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-sand-700">温度 (创造性 0-1)</label>
                    <input
                      type="number" min="0" max="1" step="0.05"
                      className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-sand-700">Endpoint</label>
                  <input
                    className="w-full mt-1 px-3 py-2 rounded-xl bg-white/85 border border-sand-300/60 outline-none focus:ring-2 focus:ring-sand-400 font-mono text-xs"
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)} />
                  <div className="text-[11px] text-sand-700 mt-1">默认是 DashScope 兼容模式; 如有代理可改。</div>
                </div>
                <label className="flex items-center gap-2 text-sm text-sand-800 cursor-pointer">
                  <input type="checkbox" checked={fallback} onChange={(e) => setFallback(e.target.checked)}
                         className="rounded border-sand-400" />
                  <span>千问失败时自动回落到模拟模式</span>
                </label>
                <div className="flex gap-2 pt-2">
                  <button className="sand-button sand-button-ghost text-sm" onClick={testQwen} disabled={testing}>
                    {testing ? '测试中…' : '🔌 测试连接'}
                  </button>
                  <button className="sand-button" onClick={save}>保存设置</button>
                </div>
                {testResult && (
                  <div className={`rounded-xl p-3 text-sm ${testResult.ok ? 'bg-oasis-500/15 text-oasis-600' : 'bg-terra-500/15 text-terra-600'}`}>
                    {testResult.ok ? '✅ 连接成功! ' : '❌ 连接失败: '}
                    <span className="text-sand-900">{testResult.text}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {provider === 'mock' && (
            <div className="paper-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🎭</span>
                <span className="font-display text-lg text-sand-900">模拟模式</span>
              </div>
              <p className="text-sm text-sand-800 leading-relaxed">
                AI 回复使用前端写好的引导性模板 + 关键词匹配 + 老师上传的知识库片段。<br/>
                <b>零网络请求,所有内容在浏览器里运行。</b>适合演示、网络受限的课堂、产品开发期。
              </p>
              <button className="sand-button mt-3" onClick={save}>保存设置</button>
            </div>
          )}

          {provider === 'proxy' && (
            <div className="paper-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🛡</span>
                <span className="font-display text-lg text-sand-900">后端代理模式</span>
                <Chip variant="gold">生产推荐</Chip>
              </div>
              <p className="text-sm text-sand-800 leading-relaxed">
                浏览器调用本站的 <code className="bg-sand-100 px-1 rounded">/api/chat</code>，
                由 <code className="bg-sand-100 px-1 rounded">server.js</code> 从 <code className="bg-sand-100 px-1 rounded">.env</code> 读取 API Key 后转发千问。
                <b>API Key 永远不出现在前端代码或浏览器</b>，符合生产规范。
              </p>
              <div className="divider-sand" />
              <div className="space-y-2 text-xs text-sand-700">
                <div>📂 <b>配置文件:</b> <code className="bg-sand-100 px-1 rounded">/Users/yangyang/Desktop/sand-world-platform/.env</code></div>
                <div>🔑 <b>需要的变量:</b> <code className="bg-sand-100 px-1 rounded">QWEN_API_KEY=sk-xxx</code></div>
                <div>📡 <b>端点:</b> <code className="bg-sand-100 px-1 rounded">{AI_CONFIG.proxy.endpoint}</code></div>
                <div>🚀 <b>启动后端:</b> <code className="bg-sand-100 px-1 rounded">node server.js</code></div>
              </div>
              <div className="divider-sand" />
              <label className="flex items-center gap-2 text-sm text-sand-800 cursor-pointer">
                <input type="checkbox" checked={fallback} onChange={(e) => setFallback(e.target.checked)} className="rounded border-sand-400" />
                <span>后端失败时自动回落到模拟模式</span>
              </label>
              <div className="flex gap-2 pt-3">
                <button className="sand-button sand-button-ghost text-sm" onClick={testProxy} disabled={testing}>
                  {testing ? '测试中…' : '🔌 测试代理 → 千问'}
                </button>
                <button className="sand-button" onClick={save}>保存设置</button>
              </div>
              {testResult && testResult.channel === 'proxy' && (
                <div className={`mt-3 rounded-xl p-3 text-sm ${testResult.ok ? 'bg-oasis-500/15 text-oasis-600' : 'bg-terra-500/15 text-terra-600'}`}>
                  {testResult.ok ? '✅ 代理 → 千问 链路通: ' : '❌ 代理失败: '}
                  <span className="text-sand-900">{testResult.text}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右: 部署说明 + system prompt 预览 */}
        <div className="lg:col-span-5 space-y-4">
          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-2">🚀 部署到生产</div>
            <div className="space-y-2 text-sm text-sand-800 leading-relaxed">
              <div><b>1. 本机:</b> <code className="bg-sand-100 px-1 rounded">node server.js</code>，前后端同源,自动走代理。</div>
              <div><b>2. 云平台</b> (Vercel / Railway / Render / 自家 VM):</div>
              <div className="pl-4">• 上传整个 <code className="bg-sand-100 px-1 rounded">sand-world-platform</code> 目录</div>
              <div className="pl-4">• 在平台控制台设置环境变量 <code className="bg-sand-100 px-1 rounded">QWEN_API_KEY</code></div>
              <div className="pl-4">• 入口指向 <code className="bg-sand-100 px-1 rounded">server.js</code></div>
              <div className="pl-4">• 启动命令 <code className="bg-sand-100 px-1 rounded">node server.js</code></div>
              <div><b>3. </b><code className="bg-sand-100 px-1 rounded">.env</code> 已在 <code className="bg-sand-100 px-1 rounded">.gitignore</code> 中,不会被推上仓库。</div>
            </div>
          </div>

          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-2">🔧 换其他模型 (DeepSeek / GPT / 文心)</div>
            <div className="text-xs text-sand-700 mb-2">改 <code className="bg-sand-100 px-1 rounded">server.js</code> 里的 <code className="bg-sand-100 px-1 rounded">QWEN_ENDPOINT</code> 和 <code className="bg-sand-100 px-1 rounded">QWEN_MODEL</code> 环境变量即可。请求体已是 OpenAI 兼容格式。</div>
            <pre className="text-[10px] bg-sand-900 text-sand-50 p-3 rounded-xl overflow-auto leading-relaxed">{`// .env
QWEN_API_KEY=sk-xxx
QWEN_ENDPOINT=https://your-llm-api/v1/chat/completions
QWEN_MODEL=qwen-plus  # or your-model-name
QWEN_TEMPERATURE=0.75
QWEN_MAX_TOKENS=700`}</pre>
          </div>

          <div className="paper-card p-5">
            <div className="font-display text-lg text-sand-900 mb-2">📝 当前 system prompt 预览</div>
            <div className="text-xs text-sand-700 mb-2">
              老沙 (沙盘组｜地质沙盘工程师) 在 <b>学的地层</b> 阶段、有 1 篇资料、学生 "陆星瑶" 登录时，得到的提示词如下：
            </div>
            <pre className="text-[10px] bg-sand-50 border border-sand-300/40 p-3 rounded-xl overflow-auto leading-relaxed text-sand-900 max-h-[300px] whitespace-pre-wrap">{typeof assembleSystemPrompt === 'function' ? assembleSystemPrompt({
              mode: 'stage', roleId: 'sand-table', stage: 'learn',
              knowledge: [{ title: '认识地形', tag: '资料包', content: '通过卫星图、立体图、剖面图，识别5种基本地形：山地、平原、高原、盆地、丘陵。' }],
              student: { name: '陆星瑶', class: '五1班' }
            }) : '(prompts.js 未加载)'}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   教师: 角色调参 (人格/口吻/口头禅/专业/示例)
   ============================================================ */

function TeacherAgentTune({ state, setState, initialRoleId, registerNavigationGuard, onBack, toast }) {
  const [activeRoleId, setActiveRoleId] = useState(() => ROLES_BY_ID[initialRoleId] ? initialRoleId : ROLES[0].id);
  const role  = ROLES_BY_ID[activeRoleId];
  const baseV = VOICES[activeRoleId];
  const baseP = ROLE_PROMPTS[activeRoleId];
  const override = (state.agentOverrides || {})[activeRoleId] || {};

  // 表单字段
  const [form, setForm] = useState({});
  const savedFormRef = useRef(null);
  useEffect(() => {
    const nextForm = {
      nickname:    override.nickname    ?? baseV.nickname,
      catchphrase: override.catchphrase ?? baseV.catchphrase,
      voice:       override.voice       ?? baseV.voice,
      mission:     override.mission     ?? role.mission,
      identity:    override.identity    ?? baseP.identity,
      expertise:   (override.expertise  ?? baseP.expertise).join('\n'),
      signature:   override.signature   ?? baseP.signature,
      taboo:       (override.taboo      ?? baseP.taboo).join('\n'),
      patterns:    (override.patterns   ?? baseP.patterns).join('\n'),
      shots:       JSON.stringify(override.shots ?? baseP.shots, null, 2),
    };
    savedFormRef.current = JSON.stringify(nextForm);
    setForm(nextForm);
  }, [activeRoleId, state.agentOverrides]);

  const isDirty = savedFormRef.current !== null && JSON.stringify(form) !== savedFormRef.current;
  function confirmLeave() {
    return !isDirty || confirm('当前角色的修改还没有保存，确定放弃这些修改并离开吗？');
  }
  useEffect(() => {
    registerNavigationGuard?.(confirmLeave);
    const beforeUnload = e => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      registerNavigationGuard?.(null);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [isDirty, registerNavigationGuard]);
  function selectRole(id) {
    if (id === activeRoleId || !confirmLeave()) return;
    setActiveRoleId(id);
  }

  function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function save() {
    let shotsArr;
    try { shotsArr = JSON.parse(form.shots); }
    catch (e) { toast('示例对话不是有效 JSON: ' + e.message, 'error'); return; }

    const newOverride = {
      nickname:    form.nickname,
      catchphrase: form.catchphrase,
      voice:       form.voice,
      mission:     form.mission,
      identity:    form.identity,
      expertise:   form.expertise.split('\n').map(x => x.trim()).filter(Boolean),
      signature:   form.signature,
      taboo:       form.taboo.split('\n').map(x => x.trim()).filter(Boolean),
      patterns:    form.patterns.split('\n').map(x => x.trim()).filter(Boolean),
      shots:       shotsArr,
    };
    setState(s => ({
      ...s,
      agentOverrides: { ...(s.agentOverrides || {}), [activeRoleId]: newOverride },
    }));
    toast(`已保存「${role.roleName}」的修改`, 'success');
  }

  function resetToDefault() {
    if (!confirm(`确定把「${role.roleName}」恢复到系统默认吗？`)) return;
    setState(s => {
      const o = { ...(s.agentOverrides || {}) };
      delete o[activeRoleId];
      return { ...s, agentOverrides: o };
    });
    toast(`已恢复默认`, 'success');
  }

  return (
    <div className="mt-2 fade-up">
      <div className="paper-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onBack}>← 返回</button>
            <div>
              <div className="font-display text-xl text-sand-900">🎭 角色智能体调参</div>
              <div className="text-xs text-sand-700">用自然语言修改任何字段。保存后下一轮对话立即生效。不需要写代码。</div>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {ROLES.map(r => {
            const modified = !!(state.agentOverrides && state.agentOverrides[r.id]);
            return (
              <button key={r.id}
                      className={`rounded-2xl p-2 border ${r.id === activeRoleId ? 'border-sand-500 bg-sand-100/80' : 'border-sand-300/30 hover:bg-sand-50/70'} transition relative`}
                      onClick={() => selectRole(r.id)}>
                {modified && <span className="absolute top-1 right-1 bg-oasis-500 w-2 h-2 rounded-full" />}
                <div className="flex justify-center"><RoleCharacter role={r} size={60} animate={false} /></div>
                <div className="text-[10px] text-sand-800 mt-1 truncate text-center">{r.teamName}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        <div className="lg:col-span-4">
          <div className="paper-card p-5 lg:sticky lg:top-4">
            <div className="flex flex-col items-center">
              <RoleCharacter role={role} size={150} />
              <div className="text-center mt-3">
                <div className="text-xs text-sand-700 tracking-widest">{role.teamName} · {role.className}</div>
                <div className="font-display text-2xl text-sand-900 mt-1">{form.nickname || role.roleName}</div>
                <div className="text-sand-800 italic text-sm mt-2">「{form.catchphrase}」</div>
              </div>
            </div>
            <div className="divider-sand"/>
            <div className="text-xs text-sand-700 leading-relaxed">{form.mission}</div>
            <div className="divider-sand"/>
            <div className="text-xs text-sand-700 mb-1">这是当前 system prompt 的核心人格部分：</div>
            <pre className="text-[10px] bg-sand-50 border border-sand-300/40 p-2 rounded-lg overflow-auto leading-relaxed text-sand-900 max-h-[200px] whitespace-pre-wrap">{form.identity}</pre>
            <div className="divider-sand"/>
            <div className="flex gap-2">
              {Object.keys(override).length > 0 && (
                <button className="sand-button sand-button-ghost text-xs flex-1" onClick={resetToDefault}>♻️ 恢复默认</button>
              )}
              <button className="sand-button text-sm flex-1" onClick={save}>💾 保存修改</button>
            </div>
            <div className="text-[11px] text-sand-700 mt-2">
              保存后,所有人和{role.roleName}的下一次对话立即使用你的新设定。
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-4">
          <FieldEditor label="爱称" desc="孩子可以怎么叫这个智能体" value={form.nickname} onChange={v => update('nickname', v)} />
          <FieldEditor label="口头禅" desc="智能体常说的一句话(只是偶尔用,别每次)" value={form.catchphrase} onChange={v => update('catchphrase', v)} />
          <FieldEditor label="口吻特征" desc="一句话描述说话风格" value={form.voice} onChange={v => update('voice', v)} />
          <FieldEditor label="使命" desc="智能体存在的目的 / 任务" value={form.mission} onChange={v => update('mission', v)} multiline />
          <FieldEditor label="身份背景 (核心)" desc="第一人称小传。决定了智能体的'我是谁'" value={form.identity} onChange={v => update('identity', v)} multiline rows={6} />
          <FieldEditor label="擅长的事 (每行一条)" desc="列出 3-7 条专业能力" value={form.expertise} onChange={v => update('expertise', v)} multiline rows={6} />
          <FieldEditor label="说话方式" desc="句式、用词、节奏的描述" value={form.signature} onChange={v => update('signature', v)} multiline rows={3} />
          <FieldEditor label="绝不做的事 (每行一条)" desc="taboo 清单,智能体不能逾越的红线" value={form.taboo} onChange={v => update('taboo', v)} multiline rows={4} />
          <FieldEditor label="常用引导问句 (每行一条)" desc="智能体的提问模板池,变着用" value={form.patterns} onChange={v => update('patterns', v)} multiline rows={5} />
          <FieldEditor label="少样本示例 (JSON 数组)" desc='格式: [{"q":"学生问","a":"智能体答"}]' value={form.shots} onChange={v => update('shots', v)} multiline rows={8} mono />
        </div>
      </div>
    </div>
  );
}

/* 通用字段编辑器 */
function FieldEditor({ label, desc, value, onChange, multiline, rows = 2, mono }) {
  return (
    <div className="paper-card p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div>
          <div className="font-medium text-sand-900 text-sm">{label}</div>
          <div className="text-[11px] text-sand-700">{desc}</div>
        </div>
        <div className="text-[10px] text-sand-700">{(value || '').length} 字</div>
      </div>
      {multiline ? (
        <textarea
          rows={rows}
          className={`w-full px-3 py-2 rounded-xl bg-white/85 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 resize-y ${mono ? 'font-mono text-[12px]' : 'text-sm'}`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="w-full px-3 py-2 rounded-xl bg-white/85 border border-sand-300/50 outline-none focus:ring-2 focus:ring-sand-400 text-sm"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

/* ============================================================
   教师: 武器调参 (工作流程/输出格式/提示词加成)
   ============================================================ */

function TeacherWeaponTune({ state, setState, initialWeaponId, registerNavigationGuard, onBack, toast }) {
  const [activeId, setActiveId] = useState(() => WEAPONS_BY_ID[initialWeaponId] ? initialWeaponId : WEAPONS[0].id);
  const w = WEAPONS_BY_ID[activeId];
  const override = (state.weaponOverrides || {})[activeId] || {};

  const [form, setForm] = useState({});
  const savedFormRef = useRef(null);
  useEffect(() => {
    const nextForm = {
      tagline:       override.tagline       ?? w.tagline,
      longTagline:   override.longTagline   ?? w.longTagline,
      problemSolved: override.problemSolved ?? w.problemSolved,
      starters:      (override.starters     ?? w.starters).join('\n'),
      promptAddon:   override.promptAddon   ?? w.promptAddon,
    };
    savedFormRef.current = JSON.stringify(nextForm);
    setForm(nextForm);
  }, [activeId, state.weaponOverrides]);

  const isDirty = savedFormRef.current !== null && JSON.stringify(form) !== savedFormRef.current;
  function confirmLeave() {
    return !isDirty || confirm('当前工具的修改还没有保存，确定放弃这些修改并离开吗？');
  }
  useEffect(() => {
    registerNavigationGuard?.(confirmLeave);
    const beforeUnload = e => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      registerNavigationGuard?.(null);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [isDirty, registerNavigationGuard]);
  function selectWeapon(id) {
    if (id === activeId || !confirmLeave()) return;
    setActiveId(id);
  }

  function update(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function save() {
    const newOverride = {
      tagline: form.tagline,
      longTagline: form.longTagline,
      problemSolved: form.problemSolved,
      starters: form.starters.split('\n').map(x => x.trim()).filter(Boolean),
      promptAddon: form.promptAddon,
    };
    setState(s => ({
      ...s,
      weaponOverrides: { ...(s.weaponOverrides || {}), [activeId]: newOverride },
    }));
    toast(`已保存「${w.name}」的修改`, 'success');
  }

  function resetToDefault() {
    if (!confirm(`确定把「${w.name}」恢复到系统默认吗？`)) return;
    setState(s => {
      const o = { ...(s.weaponOverrides || {}) };
      delete o[activeId];
      return { ...s, weaponOverrides: o };
    });
    toast(`已恢复默认`, 'success');
  }

  return (
    <div className="mt-2 fade-up">
      <div className="paper-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onBack}>← 返回</button>
            <div>
              <div className="font-display text-xl text-sand-900">🪖 武器智能体调参</div>
              <div className="text-xs text-sand-700">调整武器的工作流程、输出格式、启动语。所有 9 个角色装备这把武器时都按你设定的方法工作。</div>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-8 gap-2">
          {WEAPONS.map(ww => {
            const modified = !!(state.weaponOverrides && state.weaponOverrides[ww.id]);
            return (
              <button key={ww.id}
                      className={`rounded-2xl p-2 border ${ww.id === activeId ? 'border-sand-500 bg-sand-100/80' : 'border-sand-300/30 hover:bg-sand-50/70'} transition relative`}
                      onClick={() => selectWeapon(ww.id)}>
                {modified && <span className="absolute top-1 right-1 bg-oasis-500 w-2 h-2 rounded-full" />}
                <div className="flex justify-center"><WeaponIcon weapon={ww} size={50} /></div>
                <div className="text-[10px] text-sand-800 mt-1 truncate text-center">{ww.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-5">
        <div className="lg:col-span-4">
          <div className="paper-card p-5 lg:sticky lg:top-4">
            <div className="flex flex-col items-center">
              <WeaponIcon weapon={w} size={120} />
              <div className="text-center mt-3">
                <div className="text-xs text-sand-700 tracking-widest">{w.aliasMethod}</div>
                <div className="font-display text-2xl text-sand-900 mt-1">{w.name}</div>
                <div className="text-xs text-sand-700 mt-1">大脑工厂 · {FACTORY_STAGES[w.stage].name} · {FACTORY_STAGES[w.stage].factoryRole}</div>
                <div className="text-sand-800 text-sm mt-2 italic">「{form.tagline}」</div>
              </div>
            </div>
            <div className="divider-sand"/>
            <div className="text-xs text-sand-700 mb-1">解决的问题：</div>
            <div className="text-sm text-sand-900 bg-white/55 rounded-lg p-2">{form.problemSolved}</div>
            <div className="divider-sand"/>
            <div className="text-xs text-sand-700 mb-1">适合使用场景：</div>
            <ul className="text-xs text-sand-800 list-disc pl-4 space-y-0.5">
              {w.fitFor.map((f,i) => <li key={i}>{f}</li>)}
            </ul>
            <div className="divider-sand"/>
            <div className="flex gap-2">
              {Object.keys(override).length > 0 && (
                <button className="sand-button sand-button-ghost text-xs flex-1" onClick={resetToDefault}>♻️ 恢复默认</button>
              )}
              <button className="sand-button text-sm flex-1" onClick={save}>💾 保存修改</button>
            </div>
            <div className="text-[11px] text-sand-700 mt-2">
              保存后,任何角色装备「{w.name}」时立即使用你的新设定。
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-4">
          <FieldEditor label="短标语 (卡片上显示)" desc="一句话描述武器" value={form.tagline} onChange={v => update('tagline', v)} />
          <FieldEditor label="长描述" desc="孩子按下武器时的说明" value={form.longTagline} onChange={v => update('longTagline', v)} multiline rows={2} />
          <FieldEditor label="解决的问题" desc="这把武器主要应对什么困境" value={form.problemSolved} onChange={v => update('problemSolved', v)} multiline rows={2} />
          <FieldEditor label="孩子的快捷输入提示 (每行一条)" desc="点击 chip 自动填到输入框" value={form.starters} onChange={v => update('starters', v)} multiline rows={4} />
          <FieldEditor
            label="武器提示词加成 (核心)"
            desc="装备这把武器时,会追加到 system prompt 末尾。这里规定了工作流程、输出格式、绝不做的事。修改这里就能完全改变武器的行为方式。"
            value={form.promptAddon} onChange={v => update('promptAddon', v)} multiline rows={20} mono />
        </div>
      </div>
    </div>
  );
}

function TeacherOverview({ state, setSubView }) {
  const totalKnowledge = Object.values(state.knowledge || {}).reduce((a, v) => a + v.length, 0);
  const totalAgentOverrides = Object.keys(state.agentOverrides || {}).length;
  const totalWeaponOverrides = Object.keys(state.weaponOverrides || {}).length;
  const publishedShowcase = (state.showcase || []).filter(x => x.published).length;

  return (
    <div className="mt-2 fade-up space-y-5">
      {/* 顶部数据 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button className="text-left" onClick={() => setSubView({kind:'agent-tune'})}>
          <StatCard label="角色智能体" value={`${totalAgentOverrides} / ${ROLES.length}`} icon="🎭" sub={`${totalAgentOverrides} 个已修改`} />
        </button>
        <button className="text-left" onClick={() => setSubView({kind:'weapon-tune'})}>
          <StatCard label="武器智能体" value={`${totalWeaponOverrides} / ${WEAPONS.length}`} icon="🪖" sub={`${totalWeaponOverrides} 把已修改`} />
        </button>
        <button className="text-left" onClick={() => setSubView({kind:'knowledge'})}>
          <StatCard label="知识库篇数" value={totalKnowledge} icon="📚" sub="供智能体参考" />
        </button>
        <button className="text-left" onClick={() => setSubView({kind:'showcase'})}>
          <StatCard label="成果展" value={publishedShowcase} icon="🌾" sub="件已发布" />
        </button>
      </div>

      {/* 调参中心说明 */}
      <div className="paper-card p-6">
        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <div className="font-display text-xl text-sand-900 mb-1">🎭 角色调参中心</div>
            <p className="text-sm text-sand-800 leading-relaxed">
              九位项目智能体（老沙、老吴、金姐、夯哥、林姐、导导、蜥博士、时先生、画姐）的人格、口吻、爱称、专业、示例对话——全部可以用自然语言修改，无需写代码。
            </p>
            <button className="sand-button mt-3 text-sm" onClick={() => setSubView({kind:'agent-tune'})}>进入角色调参 →</button>
          </div>
          <div>
            <div className="font-display text-xl text-sand-900 mb-1">🪖 武器调参中心</div>
            <p className="text-sm text-sand-800 leading-relaxed">
              八把 AI 学习力武器（零件分离机、全息扫描笔、深度寻宝铲、灵感探照灯、发光记忆贴、记忆防坠网、百变测试仪、任务导航仪）的工作流程、输出格式、提示词——按需调整。
            </p>
            <button className="sand-button mt-3 text-sm" onClick={() => setSubView({kind:'weapon-tune'})}>进入武器调参 →</button>
          </div>
          <div>
            <div className="font-display text-xl text-sand-900 mb-1">📚 知识库管理</div>
            <p className="text-sm text-sand-800 leading-relaxed">
              为每个角色填写课程要点，智能体会参考这些文字。文档和图片可作为教师核对用的附件；附件不会自动被 AI 读取。
            </p>
            <button className="sand-button mt-3 text-sm" onClick={() => setSubView({kind:'knowledge'})}>进入知识库 →</button>
          </div>
        </div>
      </div>

      {/* 9 角色快速概览 */}
      <div className="paper-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-xl text-sand-900">九位角色智能体</div>
            <div className="text-xs text-sand-700">点开任意一位进入调参界面，能看到当前预设的全部内容。</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 lg:grid-cols-3 gap-3">
          {ROLES.map(r => {
            const v = VOICES[r.id];
            const modified = !!(state.agentOverrides && state.agentOverrides[r.id]);
            const kbN = (state.knowledge?.[r.id] || []).length;
            return (
              <button key={r.id}
                      className={`paper-card p-3 text-left hover:translate-y-[-2px] transition ${modified ? 'border-oasis-500/50' : ''}`}
                      onClick={() => setSubView({kind:'agent-tune', roleId: r.id})}>
                <div className="flex items-start gap-2.5">
                  <div className="shrink-0"><RoleCharacter role={r} size={60} animate={false} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-display text-base text-sand-900 truncate">{r.roleName}</div>
                      {modified && <Chip variant="green">已修改</Chip>}
                    </div>
                    <div className="text-xs text-sand-700">{r.teamName} · 爱称 {v?.nickname}</div>
                    <div className="text-[11px] text-sand-700 italic truncate mt-0.5">「{v?.catchphrase}」</div>
                    <div className="text-[11px] text-sand-700 mt-1">📚 {kbN} 篇资料</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 8 武器快速概览 */}
      <div className="paper-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display text-xl text-sand-900">八把武器智能体</div>
            <div className="text-xs text-sand-700">学习方法的"做事说明书"——可以修改工作流程、输出格式、启发问题。</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {WEAPONS.map(w => {
            const modified = !!(state.weaponOverrides && state.weaponOverrides[w.id]);
            return (
              <button key={w.id}
                      className={`paper-card p-3 text-left flex flex-col items-center hover:translate-y-[-2px] transition ${modified ? 'border-oasis-500/50' : ''}`}
                      onClick={() => setSubView({kind:'weapon-tune', weaponId: w.id})}>
                <WeaponIcon weapon={w} size={48} />
                <div className="text-sand-900 font-display text-sm mt-2 text-center">{w.name}</div>
                <div className="text-[11px] text-sand-700 text-center mt-0.5">{w.aliasMethod}</div>
                {modified && <Chip variant="green">已修改</Chip>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, sub }) {
  return (
    <div className="paper-card p-4 flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
           style={{ background: 'linear-gradient(135deg,#f7e1ae,#cf9d52)' }}>{icon}</div>
      <div className="leading-tight">
        <div className="text-xs text-sand-700">{label}</div>
        <div className="font-display text-3xl text-sand-900">{value}</div>
        {sub && <div className="text-xs text-sand-700">{sub}</div>}
      </div>
    </div>
  );
}

function TeacherShowcaseManager({ state, setState, onBack, toast }) {
  const list = state.showcase;
  function togglePublish(id) {
    setState(s => ({ ...s, showcase: s.showcase.map(it => it.id === id ? { ...it, published: !it.published } : it) }));
  }
  function remove(id) {
    const item = list.find(it => it.id === id);
    if (!item || !confirm(`确定删除「${item.title}」吗？删除后无法在这里恢复；如果只是暂时隐藏，请使用「下架」。`)) return;
    setState(s => ({ ...s, showcase: s.showcase.filter(it => it.id !== id) }));
    toast('作品已删除', 'success');
  }

  return (
    <div className="mt-2 fade-up">
      <div className="paper-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onBack}>← 返回</button>
            <div>
              <div className="font-display text-xl text-sand-900">🌾 成果展管理</div>
              <div className="text-xs text-sand-700">仅"已发布"作品对嘉宾可见。</div>
            </div>
          </div>
          <Chip variant="green">{list.filter(x=>x.published).length} 件已发布 · 共 {list.length}</Chip>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {list.map(item => {
            const role = ROLES_BY_ID[item.roleId];
            return (
              <div key={item.id} className="paper-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-base" style={{ background: role.bg }}>{role.emoji}</div>
                    <div className="leading-tight">
                      <div className="text-sm font-medium text-sand-900">{item.title}</div>
                      <div className="text-xs text-sand-700">{item.studentName} · {role.fullName}</div>
                    </div>
                  </div>
                  <Chip variant={item.published ? 'green' : 'terra'}>{item.published ? '已发布' : '已下架'}</Chip>
                </div>
                <p className="text-xs text-sand-800 mt-2 leading-relaxed">{item.summary}</p>
                <div className="text-[11px] text-sand-700 mt-1 italic">📝 {item.teacherNote}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {item.media.map((m, i) => (
                    <span key={i} className="chip">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[m.kind] || '📎'} {m.label}</span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={() => togglePublish(item.id)}>{item.published ? '下架' : '发布'}</button>
                  <button className="text-xs text-terra-500 hover:underline" onClick={() => remove(item.id)}>删除</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   4) ShowcaseView — 公开成果展(嘉宾)
   ===================================================================== */

function LegacyShowcaseView({ state, onClose }) {
  const items = state.showcase.filter(x => x.published);
  const grouped = {};
  ROLES.forEach(r => { grouped[r.id] = items.filter(x => x.roleId === r.id); });
  const [activeRole, setActiveRole] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  return (
    <Fragment>
      <TopBar
        left={<ProjectLogo small />}
        center={<div className="text-sm text-sand-700 tracking-widest">校内 / 嘉宾通道 · 一沙一世界 · 项目成果展</div>}
        right={<button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onClose}>← 返回首页</button>}
      />
      <div className="content-max px-5 sm:px-8 pb-14">
        {/* 头条 */}
        <div className="paper-card p-7 mt-2 relative overflow-hidden fade-up">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="md:col-span-2">
              <div className="text-xs text-sand-700 tracking-widest">{PROJECT.schoolName} · {PROJECT.grade} · 跨学科项目成果</div>
              <h1 className="font-display text-4xl text-sand-900 title-shadow leading-tight">{PROJECT.name}</h1>
              <div className="font-display text-lg text-sand-800 mt-1">{PROJECT.motto}</div>
              <p className="text-sand-800 leading-relaxed mt-3">
                这里是九个项目组的学生作品。从一桌沙盘到一座北塔，从一段沙之音到一份招商白皮书——每一件都来自孩子们亲手完成的「学-问-思-辨-行」。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {ROLES.map(r => (
                  <button key={r.id} className={`chip ${activeRole === r.id ? 'gold' : ''}`} onClick={() => setActiveRole(activeRole === r.id ? null : r.id)}>
                    {r.emoji} {r.teamName} <span className="opacity-60 ml-1">{grouped[r.id].length}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="paper-card p-4">
                <div className="text-xs text-sand-700 tracking-widest">展览数据</div>
                <div className="font-display text-3xl text-sand-900">{items.length}</div>
                <div className="text-xs text-sand-700">件已发布作品</div>
                <div className="divider-sand my-2"/>
                <div className="text-xs text-sand-700 tracking-widest">九重沙之世界</div>
                <div className="font-display text-3xl text-sand-900">{ROLES.length}</div>
                <div className="text-xs text-sand-700">个跨学科角色</div>
              </div>
            </div>
          </div>
        </div>

        {/* 9 个项目组成果区 */}
        <div className="mt-6 space-y-10">
          {ROLES.filter(r => !activeRole || r.id === activeRole).map(r => (
            <div key={r.id}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-sand-soft" style={{ background: r.bg }}>
                  {r.emoji}
                </div>
                <div>
                  <div className="text-xs text-sand-700 tracking-widest">{r.className}</div>
                  <div className="font-display text-2xl text-sand-900">{r.fullName}</div>
                  <div className="text-sm text-sand-700 italic">{r.tagline}</div>
                </div>
              </div>

              {grouped[r.id].length === 0 ? (
                <div className="paper-card p-5 text-sm text-sand-700">这个项目组正在准备中——精彩作品即将抵达。</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up-stagger">
                  {grouped[r.id].map(item => (
                    <div key={item.id} className="showcase-card cursor-pointer" onClick={() => setPreviewItem(item)}>
                      <div className="h-32 relative overflow-hidden"
                           style={{ background: r.bg }}>
                        <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-50">{r.emoji}</div>
                        <div className="absolute bottom-2 left-3 right-3 flex justify-between text-white/90 text-xs">
                          <span>{item.publishedAt}</span>
                          <span className="bg-black/25 rounded-full px-2 py-0.5">{r.teamName}</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="font-display text-lg text-sand-900 leading-tight">{item.title}</div>
                        <div className="text-xs text-sand-700 mt-0.5">作者：{item.studentName}（化名为本班同学）</div>
                        <p className="text-sm text-sand-800 mt-2 line-clamp-3">{item.summary}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.media.slice(0, 4).map((m, i) => (
                            <span key={i} className="chip">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[m.kind] || '📎'} {m.label}</span>
                          ))}
                        </div>
                        {item.teacherNote && (
                          <div className="mt-3 text-[11px] text-sand-700 italic">📝 {item.teacherNote}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 招商大会展示区 */}
        <div className="paper-card p-6 mt-10 relative overflow-hidden">
          <div className="text-xs text-sand-700 tracking-widest">招商大会 · 八小一沙一世界</div>
          <div className="font-display text-2xl text-sand-900 mt-1">来自孩子的「邀请函」</div>
          <p className="text-sand-800 mt-2 leading-relaxed">
            如果你是家长、嘉宾、或合作伙伴，可以在我们的展位上：参与一场 1 分钟路演、走一段 30 步沙漏长廊、亲手夯一块土砖、把一粒沙画进画里。<br/>
            欢迎来到孩子们建起的这片"沙世界"。
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="chip green">展位区 · 9 组主题</span>
            <span className="chip gold">体验区 · 5 项动手活动</span>
            <span className="chip blue">展示区 · 学生路演每 30 分钟一场</span>
          </div>
        </div>

        <div className="text-center text-xs text-sand-700 mt-6 italic">
          隐私说明：所有作品仅展示学生姓名/化名与作品本身，不公开生日、住址、联系方式等敏感信息。
        </div>
      </div>

      {/* 作品详情 */}
      <Modal open={!!previewItem} onClose={() => setPreviewItem(null)}>
        {previewItem && <ShowcaseDetail item={previewItem} role={ROLES_BY_ID[previewItem.roleId]} onClose={() => setPreviewItem(null)} />}
      </Modal>
    </Fragment>
  );
}

function LegacyShowcaseDetail({ item, role, onClose }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: role.bg }}>{role.emoji}</div>
          <div>
            <div className="text-xs text-sand-700 tracking-widest">{role.fullName}</div>
            <div className="font-display text-2xl text-sand-900">{item.title}</div>
            <div className="text-xs text-sand-700">作者：{item.studentName} · 发布：{item.publishedAt}</div>
          </div>
        </div>
        <button className="sand-button sand-button-ghost text-xs px-3 py-1.5" onClick={onClose}>关闭</button>
      </div>
      <div className="divider-sand" />
      <p className="text-sand-900 leading-relaxed whitespace-pre-wrap">{item.summary}</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
        {item.media.map((m, i) => (
          <div key={i} className="media-thumb">
            {m.data && m.kind === 'image' ? <img src={m.data} alt={m.label}/>
              : m.data && m.kind === 'video' ? <video src={m.data} controls/>
              : <div className="text-3xl">{({image:'🖼️',video:'🎬',audio:'🎙️',doc:'📄'})[m.kind] || '📎'}</div>}
            <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-xs bg-gradient-to-t from-black/40 to-transparent text-white">{m.label}</div>
          </div>
        ))}
      </div>
      {item.teacherNote && (
        <div className="paper-card p-3 mt-4">
          <div className="text-xs text-sand-700 tracking-widest mb-1">教师寄语</div>
          <div className="text-sand-900">{item.teacherNote}</div>
        </div>
      )}
    </div>
  );
}

/* =====================================================================
   工具
   ===================================================================== */

function nowStr() {
  const d = new Date();
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function avgScore(s) {
  if (!s) return '—';
  return Math.round((s.task + s.thinking + s.evidence + s.express) / 4 * 10) / 10;
}

/* =====================================================================
   挂载
   ===================================================================== */

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
